import * as i0 from '@angular/core';
import { Directive, TemplateRef, Input, ElementRef, HostBinding, Component, ContentChildren, Injectable, ViewChildren, EventEmitter, Output, ViewChild, ViewEncapsulation, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { ActionUiService, Action } from '@lbmx/action-ui';
import { fromEvent, BehaviorSubject, Subject, of, merge } from 'rxjs';
import { tap, takeUntil, startWith, switchMap, catchError, filter, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { Chart, registerables } from 'chart.js';
import { DataSource } from '@angular/cdk/collections';
import { BehaviorSubject as BehaviorSubject$1 } from 'rxjs/internal/BehaviorSubject';
import { CdkTableModule } from '@angular/cdk/table';

class AnalyticsDirective {
    constructor(template) {
        this.template = template;
    }
    getType() {
        return this.name;
    }
}
AnalyticsDirective.decorators = [
    { type: Directive, args: [{
                selector: '[laTemplate]',
            },] }
];
AnalyticsDirective.ctorParameters = () => [
    { type: TemplateRef }
];
AnalyticsDirective.propDecorators = {
    name: [{ type: Input, args: ['laTemplate',] }]
};

class ChartDirective {
    constructor(_canvasRef) {
        this._canvasRef = _canvasRef;
    }
    get canvas() {
        return this._canvasRef.nativeElement;
    }
}
ChartDirective.decorators = [
    { type: Directive, args: [{
                selector: 'canvas[laChart]',
            },] }
];
ChartDirective.ctorParameters = () => [
    { type: ElementRef }
];

var AnalyticsStatus;
(function (AnalyticsStatus) {
    AnalyticsStatus[AnalyticsStatus["ERROR"] = -1] = "ERROR";
    AnalyticsStatus[AnalyticsStatus["READY"] = 0] = "READY";
    AnalyticsStatus[AnalyticsStatus["LOADING"] = 1] = "LOADING";
    AnalyticsStatus[AnalyticsStatus["DONE"] = 2] = "DONE";
})(AnalyticsStatus || (AnalyticsStatus = {}));
var TemplateTypes;
(function (TemplateTypes) {
    TemplateTypes["DONE"] = "done";
    TemplateTypes["ERROR"] = "error";
    TemplateTypes["FOOTER"] = "footer";
    TemplateTypes["HEADER"] = "header";
    TemplateTypes["LOADING"] = "loading";
    TemplateTypes["READY"] = "ready";
    TemplateTypes["FILTER"] = "filter";
    TemplateTypes["NO_DATA"] = "noData";
})(TemplateTypes || (TemplateTypes = {}));

class WidgetDirective {
    constructor() {
        this._gridArea = '';
    }
    get grid() {
        return this._gridArea;
    }
    set grid(gridArea) {
        this._gridArea = gridArea;
    }
}
WidgetDirective.decorators = [
    { type: Directive, args: [{
                selector: '[laWidget]',
            },] }
];
WidgetDirective.ctorParameters = () => [];
WidgetDirective.propDecorators = {
    grid: [{ type: HostBinding, args: ['style.grid-area',] }]
};

class DashboardWrapperComponent {
    constructor(actionUi) {
        this.actionUi = actionUi;
        this.styleClass = '';
        this.dashboardStatus = AnalyticsStatus;
        this.transformGridArea = (gridArea) => `"${gridArea
            .map((area) => area.map((pos) => (pos !== null ? `a_${pos}` : `.`)).join(' '))
            .join(`" "`)}"`;
        this.actionUi.state
            .pipe(tap((state) => (this.dashState = Object.assign({}, state))), tap((state) => (this.gridArea = this.transformGridArea(this.getGridArea(state)))), takeUntil(this.actionUi.shouldDestroy))
            .subscribe();
        fromEvent(window, 'resize')
            .pipe(tap(() => (this.gridArea = this.transformGridArea(this.getGridArea(this.dashState)))), takeUntil(this.actionUi.shouldDestroy))
            .subscribe();
    }
    ngAfterContentInit() {
        this.widgets.changes
            .pipe(startWith(this.widgets), tap((widgets) => setTimeout(() => widgets.forEach((widget, index) => (widget.grid = `a_${index}`)), 0)), takeUntil(this.actionUi.shouldDestroy))
            .subscribe();
        this.analyticsDirectives.forEach((directive) => {
            switch (directive.getType()) {
                case TemplateTypes.DONE:
                    this.doneTemplate = directive.template;
                    break;
                case TemplateTypes.ERROR:
                    this.errorTemplate = directive.template;
                    break;
                case TemplateTypes.LOADING:
                    this.loadingTemplate = directive.template;
                    break;
                case TemplateTypes.READY:
                    this.readyTemplate = directive.template;
                    break;
                case TemplateTypes.HEADER:
                    this.headerTemplate = directive.template;
                    break;
                case TemplateTypes.FOOTER:
                    this.footerTemplate = directive.template;
                    break;
            }
        });
    }
    get dashboardState() {
        return { $implicit: this.dashState };
    }
    getGridArea({ gridArea, mediumGridArea, smallGridArea, }) {
        var _a, _b, _c;
        return ((_b = (_a = window.innerWidth > (mediumGridArea === null || mediumGridArea === void 0 ? void 0 : mediumGridArea.breakPoint)) !== null && _a !== void 0 ? _a : smallGridArea === null || smallGridArea === void 0 ? void 0 : smallGridArea.breakPoint) !== null && _b !== void 0 ? _b : 0)
            ? gridArea
            : ((_c = window.innerWidth > (smallGridArea === null || smallGridArea === void 0 ? void 0 : smallGridArea.breakPoint)) !== null && _c !== void 0 ? _c : 0)
                ? (mediumGridArea === null || mediumGridArea === void 0 ? void 0 : mediumGridArea.gridArea) || gridArea
                : (smallGridArea === null || smallGridArea === void 0 ? void 0 : smallGridArea.gridArea) || (mediumGridArea === null || mediumGridArea === void 0 ? void 0 : mediumGridArea.gridArea) || gridArea;
    }
}
DashboardWrapperComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-dashboard-wrapper',
                template: "<div class=\"dashboard {{ styleClass }}\" data-testid=\"dashboardWrapper\">\r\n  <div class=\"header\" *ngIf=\"headerTemplate\">\r\n    <ng-container *ngTemplateOutlet=\"headerTemplate\"></ng-container>\r\n  </div>\r\n  <div class=\"body\" [style.grid-template-areas]=\"gridArea\">\r\n    <ng-container *ngIf=\"dashState.status === dashboardStatus.READY\">\r\n      <ng-container *ngTemplateOutlet=\"readyTemplate; context: dashboardState\">\r\n      </ng-container>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"dashState.status === dashboardStatus.LOADING\">\r\n      <ng-container\r\n        *ngTemplateOutlet=\"loadingTemplate; context: dashboardState\"\r\n      >\r\n      </ng-container>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"dashState.status === dashboardStatus.DONE\">\r\n      <ng-container *ngTemplateOutlet=\"doneTemplate; context: dashboardState\">\r\n      </ng-container>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"dashState.status === dashboardStatus.ERROR\">\r\n      <ng-container *ngTemplateOutlet=\"errorTemplate; context: dashboardState\">\r\n      </ng-container>\r\n    </ng-container>\r\n  </div>\r\n  <div class=\"footer\" *ngIf=\"footerTemplate\" data-testid=\"dashboardWrapperFooter\">\r\n    <ng-container *ngTemplateOutlet=\"footerTemplate\"></ng-container>\r\n  </div>\r\n</div>\r\n",
                styles: [""]
            },] }
];
DashboardWrapperComponent.ctorParameters = () => [
    { type: ActionUiService }
];
DashboardWrapperComponent.propDecorators = {
    analyticsDirectives: [{ type: ContentChildren, args: [AnalyticsDirective, { descendants: false },] }],
    widgets: [{ type: ContentChildren, args: [WidgetDirective, { descendants: true },] }],
    styleClass: [{ type: Input }]
};

var HttpMethod;
(function (HttpMethod) {
    HttpMethod["DELETE"] = "delete";
    HttpMethod["GET"] = "get";
    HttpMethod["PATCH"] = "patch";
    HttpMethod["POST"] = "post";
    HttpMethod["PUT"] = "put";
})(HttpMethod || (HttpMethod = {}));
const requestDefaults = {
    endpoint: '',
    method: HttpMethod.GET,
    body: {},
    options: {
        withCredentials: true,
    },
};
class RequestService {
    constructor(http) {
        this.http = http;
        this._baseUrl = '';
        this._client = this.http;
    }
    get baseUrl() {
        return this._baseUrl;
    }
    set baseUrl(v) {
        this._baseUrl = v;
    }
    get client() {
        return this._client;
    }
    set client(v) {
        this._client = v;
    }
    request(req) {
        const mergedOptions = Object.assign(Object.assign(Object.assign({}, requestDefaults), req), { endpoint: this._baseUrl
                ? this.urlJoin(this._baseUrl, (req === null || req === void 0 ? void 0 : req.endpoint) || '')
                : (req === null || req === void 0 ? void 0 : req.endpoint) || '' });
        return this.buildRequest(mergedOptions.endpoint, mergedOptions.method, mergedOptions.body, mergedOptions.options);
    }
    buildRequest(endpoint, httpMethod, body, options) {
        switch (httpMethod) {
            case HttpMethod.PATCH:
                return this._client.patch(endpoint, body, options);
            case HttpMethod.POST:
                return this._client.post(endpoint, body, options);
            case HttpMethod.PUT:
                return this._client.put(endpoint, body, options);
            case HttpMethod.DELETE:
                return this._client.delete(endpoint, options);
            case HttpMethod.GET:
            default:
                return this._client.get(endpoint, options);
        }
    }
    urlJoin(left, right) {
        return [left.replace(/\/$/, ''), right.replace(/^\//, '')]
            .filter((url) => !!url)
            .join('/');
    }
}
RequestService.ɵprov = i0.ɵɵdefineInjectable({ factory: function RequestService_Factory() { return new RequestService(i0.ɵɵinject(i1.HttpClient)); }, token: RequestService, providedIn: "root" });
RequestService.decorators = [
    { type: Injectable, args: [{
                providedIn: 'root',
            },] }
];
RequestService.ctorParameters = () => [
    { type: HttpClient }
];

class KpiService {
    constructor() {
        this._negative = new BehaviorSubject(false);
    }
    get isNegative() {
        return this._negative.value;
    }
    setNegative() {
        this._negative.next(true);
    }
    setPositive() {
        this._negative.next(false);
    }
    reset() {
        this._negative.next(false);
    }
}
KpiService.decorators = [
    { type: Injectable }
];
KpiService.ctorParameters = () => [];

class KpiComponent {
    constructor(kpiService, _requestSrv) {
        this.kpiService = kpiService;
        this._requestSrv = _requestSrv;
        this.filters = {};
        this.icons = [];
        this.adaptor = (res) => res;
        this.templateTypes = TemplateTypes;
    }
    ngOnChanges() {
        this.request = this.buildRequest(this.config);
    }
    ngAfterContentInit() {
        this.analyticsDirectives.forEach((directive) => {
            switch (directive.getType()) {
                case TemplateTypes.ERROR:
                    this.errorTemplate = directive.template;
                    break;
                case TemplateTypes.LOADING:
                    this.loadingTemplate = directive.template;
                    break;
                case TemplateTypes.FOOTER:
                    this.footerTemplate = directive.template;
                    break;
                case TemplateTypes.READY:
                    this.readyTemplate = directive.template;
                    break;
            }
        });
    }
    buildRequest(config) {
        const { query, endpoint } = config;
        const body = Object.assign(Object.assign({}, query), { filters: Object.assign(Object.assign({}, this.filters), ((query === null || query === void 0 ? void 0 : query.filters) || {})) });
        return this._requestSrv.request({
            endpoint,
            body,
            method: HttpMethod.POST,
        });
    }
    callback(refresh, cb) {
        cb({ refresh, index: this.handle });
    }
}
KpiComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-kpi',
                template: "<lbmx-widget-wrapper\r\n  [request]=\"request\"\r\n  [ngClass]=\"kpiService.isNegative ? 'la-negative' : 'la-positive'\"\r\n  [errorTemplate]=\"errorTemplate\"\r\n  [loadingTemplate]=\"loadingTemplate\"\r\n  [footerTemplate]=\"footerTemplate\"\r\n  [readyTemplate]=\"readyTemplate\"\r\n>\r\n  <ng-template\r\n    [laTemplate]=\"templateTypes.HEADER\"\r\n    *ngIf=\"icons.length > 0\"\r\n    let-refresh=\"refresh\"\r\n  >\r\n    <ng-container *ngFor=\"let icon of icons\">\r\n      <div class=\"pointer\">\r\n        <i\r\n          class=\"{{ icon.icon }} pointer\"\r\n          (click)=\"callback(refresh, icon.callback)\"\r\n          data-testid=\"kpiIcon\"\r\n        ></i>\r\n      </div>\r\n    </ng-container>\r\n  </ng-template>\r\n  <ng-template [laTemplate]=\"templateTypes.DONE\" let-widgetState>\r\n    <lbmx-kpi-body\r\n      [type]=\"config.type\"\r\n      [title]=\"config.title\"\r\n      [subTitle]=\"config.subTitle\"\r\n      [response]=\"widgetState.value\"\r\n      [config]=\"config\"\r\n      [adaptor]=\"adaptor\"\r\n      data-testid=\"kpiBody\"\r\n    ></lbmx-kpi-body>\r\n  </ng-template>\r\n</lbmx-widget-wrapper>\r\n",
                providers: [KpiService, RequestService],
                styles: [""]
            },] }
];
KpiComponent.ctorParameters = () => [
    { type: KpiService },
    { type: RequestService }
];
KpiComponent.propDecorators = {
    analyticsDirectives: [{ type: ContentChildren, args: [AnalyticsDirective, { descendants: false },] }],
    config: [{ type: Input }],
    filters: [{ type: Input }],
    icons: [{ type: Input }],
    handle: [{ type: Input }],
    adaptor: [{ type: Input }],
    errorTemplate: [{ type: Input }],
    loadingTemplate: [{ type: Input }],
    footerTemplate: [{ type: Input }],
    readyTemplate: [{ type: Input }]
};

const defaultWidgetState$1 = {
    value: null,
    status: AnalyticsStatus.READY,
};
const ɵ0$c = (_, state) => {
    const status = state.status < 0 ? state.status : AnalyticsStatus.DONE;
    return { status };
}, ɵ1$1 = (status) => ({ status }), ɵ2$1 = (value) => ({ value });
const widgetSchema$1 = {
    finalize: {
        command: ɵ0$c,
    },
    setStatus: { command: ɵ1$1 },
    setValue: { command: ɵ2$1 },
};

class WidgetWrapperComponent {
    constructor(actionUi) {
        this.actionUi = actionUi;
        this.styleClass = '';
        this.widgetStatus = AnalyticsStatus;
        this.fetch = new Subject();
        this.initialState = defaultWidgetState$1;
        this.actions = Action.createDispatcher(widgetSchema$1);
        this.refresh = () => {
            this.actions.setStatus.dispatch(AnalyticsStatus.READY);
            this.request && this.fetch.next();
        };
        this.actionUi.initialize(this.initialState, this.actions);
        this.actionUi.state
            .pipe(tap((state) => (this.state = Object.assign({}, state))), takeUntil(this.actionUi.shouldDestroy))
            .subscribe();
        this.fetch
            .pipe(tap(() => this.actions.setStatus.dispatch(AnalyticsStatus.LOADING)), switchMap(() => this.request.pipe(catchError((err) => {
            this.actions.setStatus.dispatch(AnalyticsStatus.ERROR);
            return of(err);
        }))), tap((value) => this.actions.setValue.dispatch(value)), tap(() => this.actions.finalize.dispatch()), takeUntil(this.actionUi.shouldDestroy))
            .subscribe();
    }
    ngAfterContentInit() {
        this.analyticsDirectives.forEach((directive) => {
            switch (directive.getType()) {
                case TemplateTypes.DONE:
                    this.doneTemplate = directive.template;
                    break;
                case TemplateTypes.ERROR:
                    this.errorTemplate = directive.template;
                    break;
                case TemplateTypes.LOADING:
                    this.loadingTemplate = directive.template;
                    break;
                case TemplateTypes.READY:
                    this.readyTemplate = directive.template;
                    break;
                case TemplateTypes.HEADER:
                    this.headerTemplate = directive.template;
                    break;
                case TemplateTypes.FOOTER:
                    this.footerTemplate = directive.template;
                    break;
            }
        });
    }
    ngOnChanges() {
        this.refresh();
    }
    get widgetState() {
        return { $implicit: this.state, refresh: this.refresh };
    }
}
WidgetWrapperComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-widget-wrapper',
                template: "<div class=\"widget {{ styleClass }}\">\r\n  <div class=\"header\" *ngIf=\"headerTemplate\" data-testid=\"widgetHeader\">\r\n    <ng-container\r\n      *ngTemplateOutlet=\"headerTemplate; context: widgetState\"\r\n    ></ng-container>\r\n  </div>\r\n  <div class=\"body\" data-testid=\"widgetBody\">\r\n    <ng-container *ngIf=\"state.status === widgetStatus.READY\">\r\n      <ng-container *ngTemplateOutlet=\"readyTemplate; context: widgetState\">\r\n      </ng-container>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"state.status === widgetStatus.LOADING\">\r\n      <ng-container *ngTemplateOutlet=\"loadingTemplate; context: widgetState\">\r\n      </ng-container>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"state.status === widgetStatus.DONE\">\r\n      <ng-container *ngTemplateOutlet=\"doneTemplate; context: widgetState\">\r\n      </ng-container>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"state.status === widgetStatus.ERROR\">\r\n      <ng-container *ngTemplateOutlet=\"errorTemplate; context: widgetState\">\r\n      </ng-container>\r\n    </ng-container>\r\n  </div>\r\n  <div class=\"footer\" *ngIf=\"footerTemplate\" data-testid=\"widgetFooter\">\r\n    <ng-container\r\n      *ngTemplateOutlet=\"footerTemplate; context: widgetState\"\r\n    ></ng-container>\r\n  </div>\r\n</div>\r\n",
                providers: [ActionUiService],
                styles: [""]
            },] }
];
WidgetWrapperComponent.ctorParameters = () => [
    { type: ActionUiService }
];
WidgetWrapperComponent.propDecorators = {
    analyticsDirectives: [{ type: ContentChildren, args: [AnalyticsDirective, { descendants: false },] }],
    request: [{ type: Input }],
    styleClass: [{ type: Input }],
    doneTemplate: [{ type: Input }],
    errorTemplate: [{ type: Input }],
    loadingTemplate: [{ type: Input }],
    readyTemplate: [{ type: Input }],
    headerTemplate: [{ type: Input }],
    footerTemplate: [{ type: Input }]
};

class BaseKpiComponent {
    constructor(router) {
        this.router = router;
        this.title = '';
        this.subTitle = '';
        this.redirect = null;
    }
    ngOnInit() {
        this.value = this.transformResponse(this.response);
    }
    transformResponse(response) {
        //transform
        return response.value;
    }
    route() {
        var _a, _b, _c, _d;
        ((_a = this.redirect) === null || _a === void 0 ? void 0 : _a.url) &&
            this.router.navigate([(_b = this.redirect) === null || _b === void 0 ? void 0 : _b.url], {
                queryParams: { filters: JSON.stringify((_d = (_c = this.redirect) === null || _c === void 0 ? void 0 : _c.filters) !== null && _d !== void 0 ? _d : {}) },
            });
    }
}
BaseKpiComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-base-kpi',
                template: "<div\r\n  class=\"center\"\r\n  [ngClass]=\"{ redirect: !!this.redirect?.url }\"\r\n  (click)=\"route()\"\r\n>\r\n  <div class=\"done\">\r\n    <h3 class=\"no-margin-top no-margin-bottom\">\r\n      {{ title }}\r\n    </h3>\r\n    <h1 class=\"margin-2\">\r\n      {{ value }}\r\n    </h1>\r\n    <p class=\"fontSize14px margin-top-5\">\r\n      {{ subTitle }}\r\n    </p>\r\n  </div>\r\n</div>\r\n",
                styles: [""]
            },] }
];
BaseKpiComponent.ctorParameters = () => [
    { type: Router }
];
BaseKpiComponent.propDecorators = {
    title: [{ type: Input }],
    subTitle: [{ type: Input }],
    response: [{ type: Input }],
    redirect: [{ type: Input }]
};

class ColoredBaseKpiComponent {
    constructor(kpiService, router) {
        this.kpiService = kpiService;
        this.router = router;
        this.title = '';
        this.subTitle = '';
        this.redirect = null;
    }
    ngOnInit() {
        this.value = this.transformResponse(this.response);
    }
    ngOnDestroy() {
        this.kpiService.reset();
    }
    transformResponse(response) {
        (response === null || response === void 0 ? void 0 : response.isNegative) && this.kpiService.setNegative();
        this.variance = response.delta;
        return response.value.toString();
    }
    route() {
        var _a, _b, _c, _d;
        ((_a = this.redirect) === null || _a === void 0 ? void 0 : _a.url) &&
            this.router.navigate([(_b = this.redirect) === null || _b === void 0 ? void 0 : _b.url], {
                queryParams: { filters: JSON.stringify((_d = (_c = this.redirect) === null || _c === void 0 ? void 0 : _c.filters) !== null && _d !== void 0 ? _d : {}) },
            });
    }
}
ColoredBaseKpiComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-colored-base-kpi',
                template: "<div\r\n  class=\"center\"\r\n  [ngClass]=\"{ redirect: !!this.redirect?.url }\"\r\n  (click)=\"route()\"\r\n>\r\n  <div class=\"done\">\r\n    <h3 class=\"no-margin-top no-margin-bottom la-widget-header\">\r\n      {{ title }}\r\n    </h3>\r\n    <h1 class=\"margin-2 la-widget-value\">\r\n      {{ value }}\r\n    </h1>\r\n    <div class=\"la-widget-sub\">     \r\n    </div> \r\n    <p class=\"fontSize14px la-widget-subtitle\">\r\n      {{ subTitle }}\r\n    </p>   \r\n  </div>\r\n</div>",
                styles: [""]
            },] }
];
ColoredBaseKpiComponent.ctorParameters = () => [
    { type: KpiService },
    { type: Router }
];
ColoredBaseKpiComponent.propDecorators = {
    title: [{ type: Input }],
    subTitle: [{ type: Input }],
    response: [{ type: Input }],
    redirect: [{ type: Input }]
};

class VarianceKpiComponent {
    constructor(kpiService, router) {
        this.kpiService = kpiService;
        this.router = router;
        this.title = '';
        this.redirect = null;
    }
    ngOnInit() {
        this.value = this.transformResponse(this.response);
    }
    ngOnDestroy() {
        this.kpiService.reset();
    }
    transformResponse(response) {
        (response === null || response === void 0 ? void 0 : response.isNegative) && this.kpiService.setNegative();
        this.variance = response.delta;
        return response.value.toString();
    }
    route() {
        var _a, _b, _c, _d;
        ((_a = this.redirect) === null || _a === void 0 ? void 0 : _a.url) &&
            this.router.navigate([(_b = this.redirect) === null || _b === void 0 ? void 0 : _b.url], {
                queryParams: { filters: JSON.stringify((_d = (_c = this.redirect) === null || _c === void 0 ? void 0 : _c.filters) !== null && _d !== void 0 ? _d : {}) },
            });
    }
}
VarianceKpiComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-variance-kpi',
                template: "<div\r\n  class=\"center\"\r\n  [ngClass]=\"{ redirect: !!this.redirect?.url }\"\r\n  (click)=\"route()\"\r\n>\r\n  <div class=\"done\">\r\n    <h3 class=\"no-margin-top no-margin-bottom la-widget-header\">\r\n      {{ title }}\r\n    </h3>\r\n    <h1 class=\"margin-2 la-widget-value\">\r\n      {{ value }}\r\n    </h1>\r\n    <div class=\"la-widget-sub\">\r\n      <p class=\"fontSize14px margin-top-5 la-widget-variance\">\r\n        {{ variance }}\r\n      </p>\r\n      <div class=\"margin-top-5\">\r\n        <i\r\n          [ngClass]=\"\r\n            kpiService.isNegative ? 'pi pi-arrow-down' : 'pi pi-arrow-up'\r\n          \"\r\n        ></i>\r\n      </div>\r\n    </div>\r\n  </div>\r\n</div>\r\n",
                styles: [""]
            },] }
];
VarianceKpiComponent.ctorParameters = () => [
    { type: KpiService },
    { type: Router }
];
VarianceKpiComponent.propDecorators = {
    title: [{ type: Input }],
    response: [{ type: Input }],
    redirect: [{ type: Input }]
};

class LoadOnceService {
    constructor() {
        this.selectedTab = new BehaviorSubject('');
    }
    get selected() {
        return this.selectedTab.value;
    }
}
LoadOnceService.decorators = [
    { type: Injectable }
];
LoadOnceService.ctorParameters = () => [];

const defaultWidgetState = {
    value: null,
    status: AnalyticsStatus.READY,
};
const ɵ0$b = (_, state) => {
    const status = state.status < 0 ? state.status : AnalyticsStatus.DONE;
    return { status };
}, ɵ1 = (status) => ({ status }), ɵ2 = (value) => ({ value });
const widgetSchema = {
    finalize: {
        command: ɵ0$b,
    },
    setStatus: { command: ɵ1 },
    setValue: { command: ɵ2 },
};

class WidgetComponent {
    constructor(actionUi, loadOnceService) {
        this.actionUi = actionUi;
        this.loadOnceService = loadOnceService;
        this.analyticsStatus = AnalyticsStatus;
        this.fetch = new Subject();
        this.initialState = defaultWidgetState;
        this.actions = Action.createDispatcher(widgetSchema);
        this.isSelected = false;
        this.selectedObs = this.loadOnceService.selectedTab.pipe(tap((selected) => (this.isSelected = this.name === selected)), takeUntil(this.actionUi.shouldDestroy));
        this.load = merge(this.loadOnceService.selectedTab, this.fetch).pipe(filter(() => {
            return this.state.status === AnalyticsStatus.READY && this.isSelected;
        }), tap(() => this.actions.setStatus.dispatch(AnalyticsStatus.LOADING)), switchMap(() => this.request.pipe(catchError((err) => {
            this.actions.setStatus.dispatch(AnalyticsStatus.ERROR);
            return of(err);
        }))), tap((value) => this.actions.setValue.dispatch(value)), tap(() => this.actions.finalize.dispatch()), takeUntil(this.actionUi.shouldDestroy));
        this.refresh = () => {
            this.actions.setStatus.dispatch(AnalyticsStatus.READY);
            this.request && this.fetch.next();
        };
        this.actionUi.initialize(this.initialState, this.actions);
        this.actionUi.state
            .pipe(tap((state) => (this.state = Object.assign({}, state))), takeUntil(this.actionUi.shouldDestroy))
            .subscribe();
    }
    ngOnInit() {
        this.selectedObs.subscribe();
        this.load.subscribe();
    }
    ngOnChanges() {
        this.refresh();
    }
    get widgetState() {
        return { $implicit: this.state, refresh: this.refresh };
    }
}
WidgetComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-widget',
                template: "<ng-container *ngIf=\"state.status === analyticsStatus.READY\">\r\n  <ng-container *ngTemplateOutlet=\"readyTemplate; context: widgetState\">\r\n  </ng-container>\r\n</ng-container>\r\n<ng-container *ngIf=\"state.status === analyticsStatus.LOADING\">\r\n  <ng-container *ngTemplateOutlet=\"loadingTemplate; context: widgetState\">\r\n  </ng-container>\r\n</ng-container>\r\n<ng-container *ngIf=\"state.status === analyticsStatus.DONE\">\r\n  <ng-container *ngTemplateOutlet=\"doneTemplate; context: widgetState\">\r\n  </ng-container>\r\n</ng-container>\r\n<ng-container *ngIf=\"state.status === analyticsStatus.ERROR\">\r\n  <ng-container *ngTemplateOutlet=\"errorTemplate; context: widgetState\">\r\n  </ng-container>\r\n</ng-container>\r\n",
                providers: [ActionUiService],
                styles: [""]
            },] }
];
WidgetComponent.ctorParameters = () => [
    { type: ActionUiService },
    { type: LoadOnceService }
];
WidgetComponent.propDecorators = {
    name: [{ type: Input }],
    request: [{ type: Input }],
    readyTemplate: [{ type: Input }],
    loadingTemplate: [{ type: Input }],
    doneTemplate: [{ type: Input }],
    errorTemplate: [{ type: Input }]
};

class TabWidgetWrapperComponent {
    constructor(loadOnceService) {
        this.loadOnceService = loadOnceService;
        this.tabs = {};
        this.styleClass = '';
        this.displayTabs = true;
        this.Object = Object;
        this.refresh = () => {
            this.widgets.forEach((widget) => widget.name === this.loadOnceService.selected && widget.refresh());
        };
    }
    ngOnInit() {
        this.loadOnceService.selectedTab.next(Object.keys(this.tabs).length > 0 ? Object.keys(this.tabs)[0] : '');
    }
    ngAfterContentInit() {
        this.analyticsDirectives.forEach((directive) => {
            switch (directive.getType()) {
                case TemplateTypes.DONE:
                    this.doneTemplate = directive.template;
                    break;
                case TemplateTypes.ERROR:
                    this.errorTemplate = directive.template;
                    break;
                case TemplateTypes.LOADING:
                    this.loadingTemplate = directive.template;
                    break;
                case TemplateTypes.READY:
                    this.readyTemplate = directive.template;
                    break;
                case TemplateTypes.HEADER:
                    this.headerTemplate = directive.template;
                    break;
                case TemplateTypes.FOOTER:
                    this.footerTemplate = directive.template;
                    break;
            }
        });
    }
    get widgetState() {
        return { refresh: this.refresh };
    }
    preserveOrder() {
        return 0;
    }
    handleTabSelection(name) {
        this.loadOnceService.selectedTab.next(name);
    }
}
TabWidgetWrapperComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-tab-widget-wrapper',
                template: "<div class=\"tab-widget {{ styleClass }}\">\r\n  <div class=\"tab-header\">\r\n    <div class=\"tabs\" *ngIf=\"Object.keys(tabs).length >= 1 && displayTabs\">\r\n      <div class=\"tab\" *ngFor=\"let tab of tabs | keyvalue: preserveOrder\">\r\n        <div\r\n          class=\"header\"\r\n          [ngClass]=\"{\r\n            selected: loadOnceService.selected === tab.key,\r\n            pointer: loadOnceService.selected !== tab.key,\r\n            'la-title': Object.keys(tabs).length === 1\r\n          }\"\r\n          (click)=\"handleTabSelection(tab.key)\"\r\n        >\r\n          {{ tab.value.label }}\r\n        </div>\r\n      </div>\r\n    </div>\r\n    <div class=\"header\" *ngIf=\"headerTemplate\">\r\n      <ng-container\r\n        *ngTemplateOutlet=\"headerTemplate; context: widgetState\"\r\n      ></ng-container>\r\n    </div>\r\n  </div>\r\n  <div class=\"body\">\r\n    <ng-container *ngFor=\"let tab of tabs | keyvalue: preserveOrder\">\r\n      <lbmx-widget\r\n        [hidden]=\"loadOnceService.selected !== tab.key\"\r\n        [name]=\"tab.key\"\r\n        [request]=\"tab.value.request\"\r\n        [readyTemplate]=\"readyTemplate\"\r\n        [loadingTemplate]=\"loadingTemplate\"\r\n        [doneTemplate]=\"doneTemplate\"\r\n        [errorTemplate]=\"errorTemplate\"\r\n      ></lbmx-widget>\r\n    </ng-container>\r\n    <ng-container *ngIf=\"Object.keys(tabs).length === 0\">\r\n      <ng-container\r\n        *ngTemplateOutlet=\"readyTemplate; context: widgetState\"\r\n      ></ng-container>\r\n    </ng-container>\r\n  </div>\r\n  <div class=\"footer\" *ngIf=\"footerTemplate\">\r\n    <ng-container\r\n      *ngTemplateOutlet=\"footerTemplate; context: widgetState\"\r\n    ></ng-container>\r\n  </div>\r\n</div>\r\n",
                providers: [LoadOnceService],
                styles: [".tab-widget{display:flex;flex-direction:column;grid-gap:.5em;gap:.5em}\n"]
            },] }
];
TabWidgetWrapperComponent.ctorParameters = () => [
    { type: LoadOnceService }
];
TabWidgetWrapperComponent.propDecorators = {
    analyticsDirectives: [{ type: ContentChildren, args: [AnalyticsDirective, { descendants: false },] }],
    widgets: [{ type: ViewChildren, args: [WidgetComponent,] }],
    tabs: [{ type: Input }],
    styleClass: [{ type: Input }],
    displayTabs: [{ type: Input }],
    doneTemplate: [{ type: Input }],
    errorTemplate: [{ type: Input }],
    loadingTemplate: [{ type: Input }],
    readyTemplate: [{ type: Input }],
    headerTemplate: [{ type: Input }],
    footerTemplate: [{ type: Input }]
};

class TabChartComponent {
    constructor(_requestSrv, router) {
        this._requestSrv = _requestSrv;
        this.router = router;
        this.chartTabs = {};
        this.filters = {};
        this.icons = [];
        this.adaptor = (res) => res;
        this.noDataTest = (res) => false;
        this.templateTypes = TemplateTypes;
    }
    ngOnChanges() {
        this.tabs = this.buildTabs(this.chartTabs);
    }
    ngAfterContentInit() {
        this.analyticsDirectives.forEach((directive) => {
            switch (directive.getType()) {
                case TemplateTypes.ERROR:
                    this.errorTemplate = directive.template;
                    break;
                case TemplateTypes.LOADING:
                    this.loadingTemplate = directive.template;
                    break;
                case TemplateTypes.FOOTER:
                    this.footerTemplate = directive.template;
                    break;
                case TemplateTypes.READY:
                    this.readyTemplate = directive.template;
                    break;
                case TemplateTypes.NO_DATA:
                    this.noDataTemplate = directive.template;
                    break;
            }
        });
    }
    buildTabs(chartTabs) {
        return Object.entries(chartTabs).reduce((tabs, [key, config]) => {
            const { label, endpoint, query, options, colorType, type, redirect, config: chartConfig, } = config;
            const body = Object.assign(Object.assign({}, query), { filters: Object.assign(Object.assign({}, this.filters), ((query === null || query === void 0 ? void 0 : query.filters) || {})) });
            const request = this._requestSrv
                .request({
                endpoint,
                body,
                method: HttpMethod.POST,
            })
                .pipe(map((response) => ({
                response: this.adaptor(response, chartConfig !== null && chartConfig !== void 0 ? chartConfig : {}, type),
                type,
                options,
                colorType,
                redirect,
                noData: this.noDataTest(response, chartConfig !== null && chartConfig !== void 0 ? chartConfig : {}, type),
            })));
            return Object.assign(Object.assign({}, tabs), { [key]: { label, request } });
        }, {});
    }
    callback(refresh, cb) {
        cb({ refresh, index: this.handle });
    }
    chartClickRequest(chartClick, redirectConfig) {
        var _a;
        (redirectConfig === null || redirectConfig === void 0 ? void 0 : redirectConfig.url) &&
            this.router.navigate([redirectConfig === null || redirectConfig === void 0 ? void 0 : redirectConfig.url], {
                queryParams: {
                    filters: JSON.stringify(Object.assign(Object.assign({}, ((_a = redirectConfig === null || redirectConfig === void 0 ? void 0 : redirectConfig.filters) !== null && _a !== void 0 ? _a : {})), (redirectConfig === null || redirectConfig === void 0 ? void 0 : redirectConfig.indexMap)
                        ? { [redirectConfig.indexMap]: chartClick === null || chartClick === void 0 ? void 0 : chartClick.index }
                        : Object.assign({}, chartClick))),
                },
            });
    }
}
TabChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-tab-chart',
                template: "<lbmx-tab-widget-wrapper\r\n  [tabs]=\"tabs\"\r\n  [errorTemplate]=\"errorTemplate\"\r\n  [loadingTemplate]=\"loadingTemplate\"\r\n  [footerTemplate]=\"footerTemplate\"\r\n  [readyTemplate]=\"readyTemplate\"\r\n  data-testid=\"tabChart\"\r\n>\r\n  <ng-template\r\n    [laTemplate]=\"templateTypes.HEADER\"\r\n    *ngIf=\"icons.length > 0\"\r\n    let-refresh=\"refresh\"\r\n  >\r\n    <ng-container *ngFor=\"let icon of icons\">\r\n      <div class=\"pointer\">\r\n        <i\r\n          class=\"{{ icon.icon }}\"\r\n          (click)=\"callback(refresh, icon.callback)\"\r\n          data-testid=\"tabChartIcon\"\r\n        ></i>\r\n      </div>\r\n    </ng-container>\r\n  </ng-template>\r\n  <ng-template [laTemplate]=\"templateTypes.DONE\" let-widgetState>\r\n    <lbmx-tab-chart-body\r\n      [type]=\"widgetState.value.type\"\r\n      [response]=\"widgetState.value.response\"\r\n      [options]=\"widgetState.value.options\"\r\n      [colorType]=\"widgetState.value.colorType\"\r\n      [noDataTemplate]=\"noDataTemplate\"\r\n      [noData]=\"widgetState.value.noData\"\r\n      (chartClick)=\"chartClickRequest($event, widgetState?.value.redirect)\"\r\n      data-testid=\"tabChartBody\"\r\n    >\r\n    </lbmx-tab-chart-body>\r\n  </ng-template>\r\n</lbmx-tab-widget-wrapper>\r\n",
                providers: [RequestService],
                styles: [":host ::ng-deep .tab-widget{display:flex;flex-direction:column;grid-gap:.5em;gap:.5em;height:100%;width:100%}:host ::ng-deep .tab-widget .body{display:flex;flex-direction:column;flex-grow:1}:host ::ng-deep .tab-widget .body lbmx-widget{display:flex;flex-direction:column;flex-grow:1}:host ::ng-deep .tab-widget .body lbmx-widget lbmx-tab-chart-body{display:flex;flex-direction:column;flex-grow:1}:host ::ng-deep .tab-widget .body lbmx-widget lbmx-tab-chart-body lbmx-tab-base-chart{display:flex;flex-direction:column;flex-grow:1}:host ::ng-deep .tab-widget .body lbmx-widget lbmx-tab-chart-body lbmx-tab-base-chart .la-chart{flex-grow:1}\n"]
            },] }
];
TabChartComponent.ctorParameters = () => [
    { type: RequestService },
    { type: Router }
];
TabChartComponent.propDecorators = {
    analyticsDirectives: [{ type: ContentChildren, args: [AnalyticsDirective, { descendants: false },] }],
    errorTemplate: [{ type: Input }],
    loadingTemplate: [{ type: Input }],
    footerTemplate: [{ type: Input }],
    readyTemplate: [{ type: Input }],
    noDataTemplate: [{ type: Input }],
    chartTabs: [{ type: Input }],
    filters: [{ type: Input }],
    icons: [{ type: Input }],
    handle: [{ type: Input }],
    adaptor: [{ type: Input }],
    noDataTest: [{ type: Input }]
};

function abbreviateNumber(value) {
    const negative = value < 0;
    const absValue = Math.abs(value);
    let newValue = `${Math.abs(value)}`;
    if (absValue >= 1000) {
        let suffixes = ['', 'K', 'M', 'B', 'T'];
        let suffixNum = Math.floor((('' + absValue.toFixed(0)).length - 1) / 3);
        let shortValue;
        for (let precision = 2; precision >= 1; precision--) {
            shortValue = parseFloat((suffixNum != 0
                ? absValue / Math.pow(1000, suffixNum)
                : absValue).toPrecision(precision));
            let dotLessShortValue = (shortValue + '').replace(/[^a-zA-Z 0-9]+/g, '');
            if (dotLessShortValue.length <= 3) {
                break;
            }
        }
        if (shortValue % 1 != 0)
            shortValue = shortValue.toFixed(1);
        newValue = shortValue + suffixes[suffixNum];
    }
    return negative ? '-' + newValue : newValue;
}

const ɵ0$a = {
    datasets: [],
};
const chartDefaults$a = {
    type: 'bar',
    data: ɵ0$a,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { grid: { display: false } },
            y: {
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
        },
        plugins: {
            legend: {
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};
const datasetDefaults$6 = (data, index, colorType) => {
    var _a;
    const color = ['#BD0024', '#6C0510', '#CCCCCC', '#878787', '#525252'];
    const projectedColors = [
        'rgba(223, 0, 42, 0.2)',
        '#DF002A',
        'rgba(223, 0, 42, 0.2)',
    ];
    const hoverBackgroundColor = '#DF002A';
    const newIndex = index % color.length;
    const projected = (_a = data === null || data === void 0 ? void 0 : data.isProjected) !== null && _a !== void 0 ? _a : false;
    const backgroundColor = !projected
        ? color[newIndex]
        : projectedColors[newIndex];
    switch (colorType) {
        case ColorType.POINT:
            return {
                label: '',
                data: [],
                backgroundColor: color,
                borderColor: color,
                hoverBackgroundColor: hoverBackgroundColor,
            };
        case ColorType.SERIES:
        default:
            const result = {
                label: '',
                data: [],
                backgroundColor: backgroundColor,
                borderColor: backgroundColor,
                hoverBackgroundColor: hoverBackgroundColor,
            };
            return result;
    }
};
var ColorType;
(function (ColorType) {
    ColorType[ColorType["SERIES"] = 0] = "SERIES";
    ColorType[ColorType["POINT"] = 1] = "POINT";
})(ColorType || (ColorType = {}));

class TabChartBodyComponent {
    constructor() {
        this.type = 'bar';
        this.colorType = ColorType.SERIES;
        this.noData = false;
        this.chartClick = new EventEmitter();
        Chart.register(...registerables);
    }
    ngOnInit() {
        var _a;
        this.data = this.transformResponse(this.response);
        this.columnConfig = ((_a = this.response) === null || _a === void 0 ? void 0 : _a.columns) ? this.response.columns : [];
    }
    transformResponse(res) {
        ///transform
        return res.data;
    }
}
TabChartBodyComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-tab-chart-body',
                template: "<ng-container *ngIf=\"!noData\" [ngSwitch]=\"type\">\r\n  <lbmx-tab-horizontal-bar-chart\r\n    *ngSwitchCase=\"'horizontalBar'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-tab-horizontal-bar-chart>\r\n  <lbmx-stacked-bar-chart\r\n    *ngSwitchCase=\"'stackedBar'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-stacked-bar-chart>\r\n  <lbmx-stacked-progress-bar-chart\r\n    *ngSwitchCase=\"'stackedProgressBar'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-stacked-progress-bar-chart>\r\n  <lbmx-stacked-horizontal-bar-chart\r\n    *ngSwitchCase=\"'stackedHorizontalBar'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-stacked-horizontal-bar-chart>\r\n  <lbmx-stacked-progress-horizontal-bar-chart\r\n    *ngSwitchCase=\"'stackedHorizontalProgressBar'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-stacked-progress-horizontal-bar-chart>\r\n  <lbmx-stacked-area-chart\r\n    *ngSwitchCase=\"'stackedArea'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-stacked-area-chart>\r\n  <lbmx-table-grid\r\n    *ngSwitchCase=\"'table-grid'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [columns]=\"columnConfig\"\r\n  ></lbmx-table-grid>\r\n  <lbmx-projected-chart\r\n    *ngSwitchCase=\"'projected'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-projected-chart>\r\n  <lbmx-click-bar-chart\r\n    *ngSwitchCase=\"'clickBar'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n    (chartClick)=\"chartClick.emit($event)\"\r\n  ></lbmx-click-bar-chart>\r\n  <lbmx-gauge-variance\r\n    *ngSwitchCase=\"'variance-gauge'\"\r\n    [data]=\"data\"\r\n  ></lbmx-gauge-variance>\r\n  <lbmx-doughnut-chart\r\n    *ngSwitchCase=\"'doughnut'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-doughnut-chart>\r\n  <lbmx-horizontal-bar-scroll-chart\r\n    *ngSwitchCase=\"'horizontalScrollBar'\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-horizontal-bar-scroll-chart>\r\n  <lbmx-tab-base-chart\r\n    *ngSwitchDefault\r\n    [type]=\"type\"\r\n    [data]=\"data\"\r\n    [colorType]=\"colorType\"\r\n    [options]=\"options\"\r\n  ></lbmx-tab-base-chart>\r\n</ng-container>\r\n<ng-container *ngIf=\"noData\">\r\n  <ng-container *ngTemplateOutlet=\"noDataTemplate\"></ng-container>\r\n</ng-container>\r\n",
                styles: [":host ::ng-deep canvas{width:100%!important}\n"]
            },] }
];
TabChartBodyComponent.ctorParameters = () => [];
TabChartBodyComponent.propDecorators = {
    type: [{ type: Input }],
    response: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }],
    noData: [{ type: Input }],
    noDataTemplate: [{ type: Input }],
    chartClick: [{ type: Output }]
};

class TabBaseChartComponent {
    constructor() {
        this.type = 'bar';
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.map((value, i) => (Object.assign(Object.assign({}, datasetDefaults$6(value, i, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults$a), { type: this.type, data: newData, options: Object.assign(Object.assign(Object.assign({}, chartDefaults$a.options), this.options), { plugins: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults$a.options.plugins), (data.datasets.length < 2
                    ? {
                        legend: {
                            display: false,
                        },
                    }
                    : {})), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
TabBaseChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-tab-base-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
TabBaseChartComponent.ctorParameters = () => [];
TabBaseChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    type: [{ type: Input }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

const ɵ0$9 = {
    datasets: [],
};
const chartDefaults$9 = {
    type: 'bar',
    data: ɵ0$9,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            y: { grid: { display: false } },
            x: {
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
        },
        indexAxis: 'y',
        plugins: {
            legend: {
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};

class TabHorizontalBarChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.map((value, i) => (Object.assign(Object.assign({}, datasetDefaults$6(value, i, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults$9), { data: newData, options: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults$9.options), { grouped: false }), this.options), { plugins: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults$9.options.plugins), (data.datasets.length < 2
                    ? {
                        legend: {
                            display: false,
                        },
                    }
                    : {})), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
TabHorizontalBarChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-tab-horizontal-bar-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
TabHorizontalBarChartComponent.ctorParameters = () => [];
TabHorizontalBarChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

class KpiBodyComponent {
    constructor() {
        this.type = '';
        this.title = '';
        this.subTitle = '';
        this.adaptor = (res) => res;
    }
    ngOnInit() {
        this.kpiResponse = this.adaptor(this.response, this.config.kpiConfig, this.type);
    }
}
KpiBodyComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-kpi-body',
                template: "<ng-container [ngSwitch]=\"type\">\r\n  <ng-container *ngSwitchCase=\"'variance'\">\r\n    <lbmx-variance-kpi\r\n      [title]=\"title\"\r\n      [response]=\"kpiResponse\"\r\n      [redirect]=\"config?.redirect ? config.redirect : null\"\r\n    >\r\n    </lbmx-variance-kpi>\r\n  </ng-container>\r\n  <ng-container *ngSwitchCase=\"'variance-subtitle'\">\r\n    <lbmx-variance-subtitle-kpi\r\n      [title]=\"title\"\r\n      [subTitle]=\"subTitle\"\r\n      [response]=\"kpiResponse\"\r\n      [redirect]=\"config?.redirect ? config.redirect : null\"\r\n    >\r\n    </lbmx-variance-subtitle-kpi>\r\n  </ng-container>\r\n  <ng-container *ngSwitchCase=\"'variance-gauge'\">\r\n    <lbmx-variance-gauge-kpi\r\n      [title]=\"title\"\r\n      [response]=\"kpiResponse\"\r\n      [redirect]=\"config?.redirect ? config.redirect : null\"\r\n    >\r\n    </lbmx-variance-gauge-kpi>\r\n  </ng-container>\r\n  <ng-container *ngSwitchDefault>\r\n    <lbmx-base-kpi\r\n      [title]=\"title\"\r\n      [subTitle]=\"subTitle\"\r\n      [response]=\"kpiResponse\"\r\n      [redirect]=\"config?.redirect ? config.redirect : null\"\r\n    >\r\n    </lbmx-base-kpi>\r\n  </ng-container>\r\n  <ng-container *ngSwitchCase=\"'tiered'\">\r\n    <lbmx-tiered-kpi\r\n      [title]=\"title\"\r\n      [subTitle]=\"subTitle\"\r\n      [response]=\"kpiResponse\"\r\n      [redirect]=\"config?.redirect ? config.redirect : null\"\r\n    >\r\n    </lbmx-tiered-kpi>\r\n  </ng-container>\r\n  <ng-container *ngSwitchCase=\"'dynamic-status'\">\r\n    <lbmx-dynamic-status-kpi\r\n      [title]=\"title\"\r\n      [response]=\"kpiResponse\"\r\n      [redirect]=\"config?.redirect ? config.redirect : null\"\r\n    >\r\n    </lbmx-dynamic-status-kpi>\r\n  </ng-container>\r\n  <ng-container *ngSwitchCase=\"'colored-base'\">\r\n    <lbmx-colored-base-kpi\r\n    [title]=\"title\"\r\n    [subTitle]=\"subTitle\"\r\n    [response]=\"kpiResponse\"\r\n    [redirect]=\"config?.redirect ? config.redirect : null\"\r\n    >\r\n    </lbmx-colored-base-kpi>\r\n  </ng-container>\r\n</ng-container>\r\n",
                styles: [""]
            },] }
];
KpiBodyComponent.ctorParameters = () => [];
KpiBodyComponent.propDecorators = {
    type: [{ type: Input }],
    response: [{ type: Input }],
    title: [{ type: Input }],
    subTitle: [{ type: Input }],
    config: [{ type: Input }],
    adaptor: [{ type: Input }]
};

const ɵ0$8 = {
    datasets: [],
};
const chartDefaults$8 = {
    type: 'bar',
    data: ɵ0$8,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { grid: { display: false }, stacked: true },
            y: {
                stacked: true,
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
        },
        plugins: {
            legend: {
                reverse: true,
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};

class StackedBarChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.reverse().map((value, i, array) => (Object.assign(Object.assign({}, datasetDefaults$6(value, array.length - 1 - i, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults$8), { data: newData, options: Object.assign(Object.assign(Object.assign({}, chartDefaults$8.options), this.options), { plugins: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults$8.options.plugins), (data.datasets.length < 2
                    ? {
                        legend: {
                            display: false,
                        },
                    }
                    : {})), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
StackedBarChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-stacked-bar-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
StackedBarChartComponent.ctorParameters = () => [];
StackedBarChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

const datasetDefaults$5 = (index, arrayLength, colorType) => {
    const color = (arrayLength === 5) ?
        (index === 4 ? ['#BD0024'] :
            (index === 3 ? ['#6C0510'] :
                (index === 2 ? ['#CCCCCC'] :
                    (index === 1 ? ['#878787'] : ['#525252'])))) :
        (arrayLength === 4) ?
            (index === 3 ? ['#BD0024'] :
                (index === 2 ? ['#6C0510'] :
                    (index === 1 ? ['#CCCCCC'] : ['#878787']))) :
            (arrayLength === 3) ?
                (index === 2 ? ['#BD0024'] :
                    (index === 1 ? ['#6C0510'] : ['#CCCCCC'])) :
                (arrayLength === 2) ?
                    (index === 1 ? ['#BD0024'] : ['#6C0510']) :
                    ['#BD0024', '#6C0510', '#CCCCCC', '#878787', '#525252'];
    const hoverBackgroundColor = '#DF002A';
    const newIndex = index % color.length;
    switch (colorType) {
        case ColorType.POINT:
            return {
                label: '',
                data: [],
                backgroundColor: color,
                borderColor: color,
                hoverBackgroundColor: hoverBackgroundColor,
                fill: true,
            };
        case ColorType.SERIES:
        default:
            return {
                label: '',
                data: [],
                backgroundColor: color[newIndex],
                borderColor: color[newIndex],
                hoverBackgroundColor: hoverBackgroundColor,
                fill: true,
            };
    }
};
const ɵ0$7 = {
    datasets: [].reverse(),
};
const chartDefaults$7 = {
    type: 'bar',
    data: ɵ0$7,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        scales: {
            x: {
                stacked: true,
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
            y: {
                grid: {
                    display: false,
                },
                stacked: true,
            },
        },
        plugins: {
            legend: {
                //reverse: true,
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};

class StackedHorizontalBarChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.reverse().map((value, i, array) => (Object.assign(Object.assign({}, datasetDefaults$5(array.length - 1 - i, array.length, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults$7), { data: newData, options: Object.assign(Object.assign(Object.assign({}, chartDefaults$7.options), this.options), { plugins: Object.assign(Object.assign(Object.assign({}, chartDefaults$7.options.plugins), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
StackedHorizontalBarChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-stacked-horizontal-bar-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
StackedHorizontalBarChartComponent.ctorParameters = () => [];
StackedHorizontalBarChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

const datasetDefaults$4 = (index, colorType) => {
    const color = ['#BD0024', '#6C0510', '#CCCCCC', '#878787', '#525252'];
    const hoverBackgroundColor = '#DF002A';
    const newIndex = index % color.length;
    switch (colorType) {
        case ColorType.POINT:
            return {
                label: '',
                data: [],
                backgroundColor: color,
                borderColor: color,
                hoverBackgroundColor: hoverBackgroundColor,
                fill: true,
            };
        case ColorType.SERIES:
        default:
            return {
                label: '',
                data: [],
                backgroundColor: color[newIndex],
                borderColor: color[newIndex],
                hoverBackgroundColor: hoverBackgroundColor,
                fill: true,
            };
    }
};
const ɵ0$6 = {
    datasets: [],
};
const chartDefaults$6 = {
    type: 'line',
    data: ɵ0$6,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { grid: { display: false } },
            y: {
                stacked: true,
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
        },
        plugins: {
            legend: {
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};

class StackedAreaChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.map((value, i) => (Object.assign(Object.assign({}, datasetDefaults$4(i, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults$6), { data: newData, options: Object.assign(Object.assign(Object.assign({}, chartDefaults$6.options), this.options), { plugins: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults$6.options.plugins), (data.datasets.length < 2
                    ? {
                        legend: {
                            display: false,
                        },
                    }
                    : {})), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
StackedAreaChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-stacked-area-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
StackedAreaChartComponent.ctorParameters = () => [];
StackedAreaChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

class Point {
    constructor(x, y) {
        this._x = x;
        this._y = y;
    }
    get x() {
        return this._x;
    }
    get y() {
        return this._y;
    }
}

class Arc {
    constructor(radius, angle) {
        this.radius = 0;
        this.angle = 0;
        this.radius = radius;
        this.angle = angle;
    }
}
class AnnularSegment {
    constructor(outerRadius, innerRadius, angle) {
        this.outerRadius = 0;
        this.innerRadius = 0;
        this.angle = 0;
        this.outerRadius = outerRadius;
        this.innerRadius = innerRadius;
        this.angle = angle;
    }
    arcEndPoint(radius, angle) {
        const sweepAngleRad = (angle * Math.PI) / 180;
        // Calculate the end point coordinates
        const x = radius * Math.sin(sweepAngleRad);
        const y = radius * Math.cos(sweepAngleRad);
        return { x, y };
    }
    calculateArcEnd(arc) {
        return arc === 'inner'
            ? this.arcEndPoint(this.innerRadius, this.angle)
            : this.arcEndPoint(this.outerRadius, this.angle);
    }
    toPath() {
        const outerArcEnd = this.calculateArcEnd('outer');
        const innerArcEnd = this.calculateArcEnd('inner');
        const svgParts = [
            `M0,${this.outerRadius}`,
            `A${this.outerRadius},${this.outerRadius} 0 0,0 ${outerArcEnd.x},${outerArcEnd.y}`,
            `L${innerArcEnd.x},${innerArcEnd.y}`,
            `A${this.innerRadius},${this.innerRadius} 0 0,1 0,${this.innerRadius}`,
            'Z',
        ];
        return svgParts.join('\n');
    }
}
class SegmentedAnnulus {
    constructor(outerRadius, innerRadius, numSegments) {
        this.number = numSegments;
        this.angle = 180 / numSegments;
        this.segment = new AnnularSegment(outerRadius, innerRadius, this.angle);
    }
    segments() {
        const angle = 180 / this.number;
        let segments = [];
        for (let i = 0; i < this.number; i++) {
            segments.push({
                offsetAngle: -90 + i * angle,
                path: this.segment.toPath(),
            });
        }
        return segments;
    }
}
// TODO: rename, merge with AnnularSegment or replace with
// an animatable segment like our upload progress indicator
/**
 * This appears to be used to generate the annulus for the
 * progress indicator.
 */
class DynamicAnnulus {
    constructor(outerRadius, innerRadius, calculator) {
        this.outerRadius = 0;
        this.innerRadius = 0;
        this.outerRadius = outerRadius;
        this.innerRadius = innerRadius;
        this.calculator = calculator;
    }
    segment() {
        const piece = new AnnularSegment(this.outerRadius, this.innerRadius, this.calculator(this.outerRadius, this.innerRadius));
        return {
            offsetAngle: 0,
            path: piece.toPath(),
        };
    }
}

const colors = ['#F2F2F2', '#CCCCCC', '#878787', '#525252', '#000000'];
class GaugeComponent {
    constructor() {
        // external
        this.delta = null;
        this.value = 0;
        this.deltaStr = '';
        this.valueStr = '';
        this.segments = 5;
        this.radialThickness = 18;
        this.colors = colors;
        this.indicatorColor = '#FFFFFF';
        this.positiveDeltaColor = '000000';
        this.negativeDeltaColor = '#BD0024';
        this.margin = 6;
        this.height = 100;
        this.width = 200;
        this.outerRadius = this.height - this.margin * 2;
        this.origin = new Point(this.width / 2, this.height - this.margin);
        this.tickLength = 2;
        this.scaleTextOffset = this.tickLength + 0.5;
        this.scaleTextSize = 4;
    }
    // ------ Reactive Values End ------
    // ------ Effects Start ------
    // need an effect for when the percentComplete changes
    // need an effect for when the number of segments changes
    // need an effect for when the delta changes
    // need an effect for when the radialThickness changes
    // ------ Effects End ------
    getIndicatorSegment() {
        const indicatorWidth = this.radialThickness / 4;
        const indicatorSegment = new DynamicAnnulus(this.outerRadius - indicatorWidth, this.outerRadius - indicatorWidth * 3, () => (this.value * 180) / 100);
        return indicatorSegment;
    }
    ngOnInit() {
        this.indicatorAnnulus = this.getIndicatorSegment();
        this.ratingSegments = new SegmentedAnnulus(this.outerRadius, this.outerRadius - this.radialThickness, this.segments).segments();
        this.deltaAbs = Math.abs(this.delta);
        this.innerRadius = this.outerRadius - this.radialThickness;
        this.percentCompleteFontSize = this.innerRadius * 0.25;
        this.deltaFontSize = this.innerRadius * 0.11;
    }
}
GaugeComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-gauge',
                template: "<svg\r\n  [attr.viewBox]=\"'0 0 200 100'\"\r\n  preserveAspectRatio=\"xMidYMid meet\"\r\n  style=\"width: 100%; height: 100%\"\r\n>\r\n  <!-- Segments Start -->\r\n  <g\r\n    *ngFor=\"let segment of ratingSegments; index as i\"\r\n    [attr.transform]=\"\r\n      'translate(' +\r\n      origin.x +\r\n      ', ' +\r\n      origin.y +\r\n      ') rotate(' +\r\n      segment.offsetAngle +\r\n      ') scale(1, -1)'\r\n    \"\r\n  >\r\n    <!-- segment -->\r\n    <path\r\n      [attr.d]=\"segment.path\"\r\n      stroke=\"none\"\r\n      [attr.fill]=\"colors[i % colors.length]\"\r\n    ></path>\r\n\r\n    <!-- tick -->\r\n    <line\r\n      [attr.x1]=\"0\"\r\n      [attr.y1]=\"outerRadius\"\r\n      [attr.x2]=\"0\"\r\n      [attr.y2]=\"outerRadius + tickLength\"\r\n      stroke=\"currentColor\"\r\n      stroke-width=\"0.5\"\r\n    ></line>\r\n\r\n    <text\r\n      [attr.text-anchor]=\"\r\n        segment.offsetAngle === 0\r\n          ? 'middle'\r\n          : segment.offsetAngle < 0\r\n          ? 'end'\r\n          : 'start'\r\n      \"\r\n      [attr.transform]=\"\r\n        'translate(0,' +\r\n        (outerRadius + scaleTextOffset) +\r\n        ') rotate(' +\r\n        segment.offsetAngle +\r\n        ') translate(0,' +\r\n        (-outerRadius - scaleTextOffset) +\r\n        ') scale(1, -1)'\r\n      \"\r\n      [attr.font-size]=\"scaleTextSize\"\r\n      x=\"0\"\r\n      [attr.y]=\"-outerRadius - scaleTextOffset\"\r\n      fill=\"currentColor\"\r\n    >\r\n      {{ (100 / segments) * i | number : \"1.0-2\" }}\r\n    </text>\r\n  </g>\r\n\r\n  <g\r\n    [attr.transform]=\"\r\n      'translate(' + origin.x + ', ' + origin.y + ') rotate(-90) scale(1, -1)'\r\n    \"\r\n  >\r\n    <path\r\n      [attr.fill]=\"indicatorColor\"\r\n      [attr.d]=\"indicatorAnnulus.segment().path\"\r\n    ></path>\r\n  </g>\r\n\r\n  <!-- \r\n    Might want to rotate this -90 and adjust from there\r\n\r\n  -->\r\n  <g\r\n    [attr.transform]=\"\r\n      'translate(' + origin.x + ', ' + origin.y + ') scale(1, -1)'\r\n    \"\r\n  >\r\n    <!-- left lower border -->\r\n    <line\r\n      [attr.x1]=\"-innerRadius\"\r\n      [attr.y1]=\"0\"\r\n      [attr.x2]=\"-outerRadius\"\r\n      [attr.y2]=\"0\"\r\n      stroke=\"none\"\r\n      stroke-width=\"0.5\"\r\n    ></line>\r\n\r\n    <!-- right lower border -->\r\n    <line\r\n      [attr.x1]=\"innerRadius\"\r\n      [attr.y1]=\"0\"\r\n      [attr.x2]=\"outerRadius\"\r\n      [attr.y2]=\"0\"\r\n      stroke=\"none\"\r\n      stroke-width=\"0.5\"\r\n    ></line>\r\n\r\n    <!-- outer border -->\r\n    <path\r\n      stroke=\"none\"\r\n      [attr.d]=\"\r\n        'M' +\r\n        -outerRadius +\r\n        ',0\r\n      A' +\r\n        outerRadius +\r\n        ',' +\r\n        outerRadius +\r\n        ' 0 0,0 ' +\r\n        outerRadius +\r\n        ',' +\r\n        0 +\r\n        ''\r\n      \"\r\n      fill=\"none\"\r\n      stroke=\"none\"\r\n      stroke-width=\"0.5\"\r\n    ></path>\r\n\r\n    <!-- inner border -->\r\n    <path\r\n      stroke=\"none\"\r\n      [attr.d]=\"\r\n        'M' +\r\n        -innerRadius +\r\n        ',0\r\n      A' +\r\n        innerRadius +\r\n        ',' +\r\n        innerRadius +\r\n        ' 0 0,0 ' +\r\n        innerRadius +\r\n        ',' +\r\n        0 +\r\n        ''\r\n      \"\r\n      fill=\"none\"\r\n      stroke=\"none\"\r\n      stroke-width=\"0.5\"\r\n    ></path>\r\n\r\n    <!-- tick -->\r\n    <line\r\n      [attr.x1]=\"outerRadius\"\r\n      [attr.y1]=\"0\"\r\n      [attr.x2]=\"outerRadius + tickLength\"\r\n      [attr.y2]=\"0\"\r\n      stroke=\"currentColor\"\r\n      stroke-width=\"0.5\"\r\n    ></line>\r\n\r\n    <text\r\n      [attr.transform]=\"'rotate(0) scale(1, -1)'\"\r\n      [attr.font-size]=\"scaleTextSize\"\r\n      [attr.x]=\"outerRadius + scaleTextOffset\"\r\n      [attr.y]=\"0\"\r\n      fill=\"currentColor\"\r\n    >\r\n      100\r\n    </text>\r\n  </g>\r\n\r\n  <!-- Segments End -->\r\n\r\n  <!-- Variance Section Start -->\r\n  <!-- Percent Complete -->\r\n  <g\r\n    [attr.transform]=\"\r\n      'translate(' + origin.x + ',' + origin.y + ') scale(1, 1)'\r\n    \"\r\n  >\r\n    <text\r\n      x=\"0\"\r\n      [attr.y]=\"-(innerRadius - percentCompleteFontSize / 2) / 2\"\r\n      text-anchor=\"middle\"\r\n      dominant-baseline=\"middle\"\r\n      fill=\"currentColor\"\r\n    >\r\n      <tspan [attr.font-size]=\"percentCompleteFontSize\">\r\n        {{ valueStr }}\r\n      </tspan>\r\n    </text>\r\n  </g>\r\n  <!-- Delta -->\r\n  <g\r\n    *ngIf=\"delta\"\r\n    [attr.transform]=\"\r\n      'translate(' + origin.x + ',' + origin.y + ') scale(1, 1)'\r\n    \"\r\n  >\r\n    <text\r\n      x=\"0\"\r\n      [attr.y]=\"-innerRadius * 0.25\"\r\n      text-anchor=\"middle\"\r\n      dominant-baseline=\"middle\"\r\n      [attr.font-size]=\"deltaFontSize\"\r\n      fill=\"currentColor\"\r\n    >\r\n      <tspan>{{ deltaStr }}</tspan>\r\n      <tspan\r\n        [attr.font-size]=\"deltaFontSize\"\r\n        [attr.fill]=\"delta < 0 ? negativeDeltaColor : positiveDeltaColor\"\r\n      >\r\n        {{ delta < 0 ? \"\uD83E\uDC6B\" : \"\uD83E\uDC69\" }}\r\n      </tspan>\r\n    </text>\r\n  </g>\r\n\r\n  <!-- Variance Section End -->\r\n</svg>\r\n"
            },] }
];
GaugeComponent.ctorParameters = () => [];
GaugeComponent.propDecorators = {
    delta: [{ type: Input }],
    value: [{ type: Input }],
    deltaStr: [{ type: Input }],
    valueStr: [{ type: Input }],
    segments: [{ type: Input }],
    radialThickness: [{ type: Input }],
    colors: [{ type: Input }],
    indicatorColor: [{ type: Input }],
    positiveDeltaColor: [{ type: Input }],
    negativeDeltaColor: [{ type: Input }]
};

class VarianceGaugeKpiComponent {
    constructor(kpiService, router) {
        this.kpiService = kpiService;
        this.router = router;
        this.title = '';
        this.redirect = null;
    }
    ngOnInit() {
        this.stringValue = this.transformResponse(this.response);
        this.value = this.response.value;
    }
    ngOnDestroy() {
        this.kpiService.reset();
    }
    transformResponse(response) {
        this.variance = response.deltaValue;
        this.valueStr = response.value.toFixed(0);
        this.varianceStr = response.delta;
        return response.value;
    }
    route() {
        var _a, _b, _c, _d;
        ((_a = this.redirect) === null || _a === void 0 ? void 0 : _a.url) &&
            this.router.navigate([(_b = this.redirect) === null || _b === void 0 ? void 0 : _b.url], {
                queryParams: { filters: JSON.stringify((_d = (_c = this.redirect) === null || _c === void 0 ? void 0 : _c.filters) !== null && _d !== void 0 ? _d : {}) },
            });
    }
}
VarianceGaugeKpiComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-variance-gauge-kpi',
                template: "<div\r\n  class=\"center\"\r\n  [ngClass]=\"{ redirect: !!this.redirect?.url }\"\r\n  (click)=\"route()\"\r\n>\r\n  <div class=\"done\">\r\n    <h3 class=\"no-margin-top no-margin-bottom la-widget-header\">\r\n      {{ title }}\r\n    </h3>\r\n    <lbmx-gauge [value]=\"value\" [delta]=\"variance\" [valueStr]=\"valueStr\" [deltaStr]=\"varianceStr\"></lbmx-gauge>\r\n  </div>\r\n</div>\r\n"
            },] }
];
VarianceGaugeKpiComponent.ctorParameters = () => [
    { type: KpiService },
    { type: Router }
];
VarianceGaugeKpiComponent.propDecorators = {
    title: [{ type: Input }],
    response: [{ type: Input }],
    redirect: [{ type: Input }]
};

var TableAlignment;
(function (TableAlignment) {
    TableAlignment["LEFT"] = "left";
    TableAlignment["CENTER"] = "center";
    TableAlignment["RIGHT"] = "right";
})(TableAlignment || (TableAlignment = {}));
// export const buildFilters;

class TableGridComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
        this.tableData = null;
        // public footer: { [key: string]: any } = null;
        this.columnsToDisplay = [];
    }
    ngOnInit() {
        this.tableData = this.data.results;
        this.columnsDefinition = this.buildColumns(this.columns);
        this.columnsToDisplay = this.buildColumnsToDisplay(this.columns);
    }
    buildColumns(columnsConfig) {
        return columnsConfig.reduce((columnsDef, { valueField, label, alignment }) => {
            return Object.assign(Object.assign({}, columnsDef), { [valueField]: {
                    label,
                    styles: Object.assign({}, this.getAlignment(alignment)),
                } });
        }, {});
    }
    getAlignment(alignment) {
        switch (alignment) {
            case TableAlignment.RIGHT:
                return { data: { 'content-end': true } };
            case TableAlignment.CENTER:
                return { data: { center: true } };
            case TableAlignment.LEFT:
            default:
                return {};
        }
    }
    buildColumnsToDisplay(columnsConfig) {
        return columnsConfig.map((columnConfig) => columnConfig.valueField);
    }
}
TableGridComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-table-grid',
                template: "<div class=\"table-container\">\r\n  <lbmx-table\r\n    [data]=\"tableData\"\r\n    [displayedColumns]=\"columnsToDisplay\"\r\n    [columnDefinitions]=\"columnsDefinition\"\r\n  ></lbmx-table>\r\n</div>\r\n",
                styles: [".table-container{max-height:400px;overflow-y:auto;overflow-x:auto;border:1px solid #ddd}.table-container table{width:100%;border-collapse:collapse}.table-container th,.table-container td{white-space:nowrap}.table-container{max-height:400px;overflow-x:auto}.table-container table{min-width:800px}\n"]
            },] }
];
TableGridComponent.ctorParameters = () => [];
TableGridComponent.propDecorators = {
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }],
    columns: [{ type: Input }]
};

class TableComponent {
    constructor() {
        this.footer = null;
        this.displayedColumns = [];
        this.columnDefinitions = null;
        this.dataSource = new ExampleDataSource([]);
    }
    set data(d) {
        this.dataSource.data.next(d);
    }
    preserveOrder() {
        return 0;
    }
}
TableComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-table',
                template: "<table cdk-table [dataSource]=\"dataSource\">\r\n  <ng-container\r\n    *ngFor=\"let column of columnDefinitions | keyvalue: preserveOrder\"\r\n    [cdkColumnDef]=\"column.key\"\r\n  >\r\n    <th\r\n      cdk-header-cell\r\n      [ngClass]=\"column.value?.styles?.header\"\r\n      *cdkHeaderCellDef\r\n    >\r\n      {{ column.value.label }}\r\n    </th>\r\n    <td\r\n      cdk-cell\r\n      [ngClass]=\"column.value?.styles?.data\"\r\n      *cdkCellDef=\"let rowData\"\r\n    >\r\n      {{ rowData[column.key] }}\r\n    </td>\r\n\r\n    <ng-container *ngIf=\"footer\">\r\n      <td\r\n        cdk-footer-cell\r\n        [ngClass]=\"column.value?.styles?.data\"\r\n        *cdkFooterCellDef\r\n      >\r\n        {{ footer[column.key] }}\r\n      </td>\r\n    </ng-container>\r\n  </ng-container>\r\n\r\n  <tr cdk-header-row *cdkHeaderRowDef=\"displayedColumns\"></tr>\r\n  <tr cdk-row *cdkRowDef=\"let row; columns: displayedColumns\"></tr>\r\n  <ng-container *ngIf=\"footer\">\r\n    <tr cdk-footer-row *cdkFooterRowDef=\"displayedColumns\"></tr>\r\n  </ng-container>\r\n</table>\r\n",
                encapsulation: ViewEncapsulation.None,
                styles: [""]
            },] }
];
TableComponent.propDecorators = {
    data: [{ type: Input }],
    footer: [{ type: Input }],
    displayedColumns: [{ type: Input }],
    columnDefinitions: [{ type: Input }]
};
class ExampleDataSource extends DataSource {
    constructor(dataSource = []) {
        super();
        this.data = new BehaviorSubject$1(dataSource);
    }
    connect() {
        return this.data;
    }
    disconnect() { }
}

class CdTableModule {
}
CdTableModule.decorators = [
    { type: NgModule, args: [{
                declarations: [TableComponent],
                imports: [CdkTableModule, CommonModule],
                exports: [TableComponent],
            },] }
];

const ɵ0$5 = {
    datasets: [],
};
const chartDefaults$5 = {
    type: 'line',
    data: ɵ0$5,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { grid: { display: false } },
            y: {
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
        },
        plugins: {
            legend: {
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};
const datasetDefaults$3 = (value, index, colorType) => {
    const normalColors = ['#bd081b', '#6C0510', '#CCCCCC'];
    const projectedColors = ['#F77689', 'rgba(247, 118, 137, 0.20)', '#CCCCCC80'];
    const color = colorType === 'PROJECTED' ? projectedColors : normalColors;
    const newIndex = index % color.length;
    const isProjected = value.isProjected || false;
    switch (colorType) {
        case ColorType.POINT:
            return {
                label: '',
                data: [],
                backgroundColor: color,
                borderColor: color,
                fill: true,
                isProjected: isProjected,
            };
        case ColorType.SERIES:
        default:
            return {
                label: '',
                data: [],
                backgroundColor: isProjected ? projectedColors[0] : color[newIndex],
                borderColor: isProjected ? projectedColors[0] : color[newIndex],
                fill: false,
                isProjected: isProjected,
            };
    }
};

class ProjectedChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: data.datasets.map((value, i, array) => {
                const isProjected = (dataset) => dataset.isProjected;
                const isFirstProjected = i === array.findIndex(isProjected);
                const isLastProjected = i ===
                    array.length - 1 - array.slice().reverse().findIndex(isProjected);
                const result = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, datasetDefaults$3(value, i, this.colorType)), value), (isProjected(value) &&
                    isFirstProjected && {
                    fill: '+1',
                    backgroundColor: 'rgba(247, 118, 137, 0.20)',
                    borderColor: 'rgba(247, 118, 137, 0.20)',
                    borderWidth: 0,
                    isProjected: true,
                })), (isProjected(value) &&
                    !isFirstProjected &&
                    !isLastProjected && {
                    fill: false,
                    backgroundColor: '#F77689',
                    borderColor: '#F77689',
                    isProjected: true,
                })), (isProjected(value) &&
                    isLastProjected && {
                    fill: '-1',
                    backgroundColor: 'rgba(247, 118, 137, 0.20)',
                    borderColor: 'rgba(247, 118, 137, 0.20)',
                    borderWidth: 0,
                    isProjected: true,
                }));
                return result;
            }) });
        return Object.assign(Object.assign({}, chartDefaults$5), { data: newData, options: Object.assign(Object.assign(Object.assign({}, chartDefaults$5.options), this.options), { plugins: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults$5.options.plugins), (data.datasets.length < 2
                    ? {
                        legend: {
                            display: false,
                        },
                    }
                    : {})), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
ProjectedChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-projected-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
ProjectedChartComponent.ctorParameters = () => [];
ProjectedChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

class GaugeVarianceComponent {
    constructor(kpiService, router) {
        this.kpiService = kpiService;
        this.router = router;
        this.title = '';
        this.redirect = null;
    }
    ngOnInit() {
        this.stringValue = this.transformResponse(this.data.config);
        this.value = this.data.config.value;
    }
    ngOnDestroy() {
        this.kpiService.reset();
    }
    transformResponse(response) {
        this.variance = response.deltaValue;
        this.valueStr = response.value.toFixed(0);
        this.varianceStr = response.delta;
        return response.value;
    }
    route() {
        var _a, _b, _c;
        if ((_a = this.redirect) === null || _a === void 0 ? void 0 : _a.url) {
            this.router.navigate([this.redirect.url], {
                queryParams: { filters: JSON.stringify((_c = (_b = this.redirect) === null || _b === void 0 ? void 0 : _b.filters) !== null && _c !== void 0 ? _c : {}) },
            });
        }
    }
}
GaugeVarianceComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-gauge-variance',
                template: "<div class=\"la-chart-container\">\r\n  <lbmx-gauge\r\n    [value]=\"value\"\r\n    [delta]=\"variance\"\r\n    [valueStr]=\"valueStr\"\r\n    [deltaStr]=\"varianceStr\"\r\n    class=\"la-chart\"\r\n  ></lbmx-gauge>\r\n</div>\r\n",
                providers: [KpiService],
                styles: [":host{display:block;width:100%;height:100%}.la-chart-container{display:flex;justify-content:center;align-items:center;width:100%;height:100%}.la-chart{width:100%;height:100%;display:flex;justify-content:center;align-items:center}\n"]
            },] }
];
GaugeVarianceComponent.ctorParameters = () => [
    { type: KpiService },
    { type: Router }
];
GaugeVarianceComponent.propDecorators = {
    title: [{ type: Input }],
    response: [{ type: Input }],
    redirect: [{ type: Input }],
    data: [{ type: Input }],
    colorType: [{ type: Input }],
    options: [{ type: Input }],
    chartCanvas: [{ type: ViewChild, args: ['chartCanvas',] }]
};

const ɵ0$4 = {
    datasets: [],
};
const chartDefaults$4 = {
    type: 'bar',
    data: ɵ0$4,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { grid: { display: false } },
            y: {
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
        },
    },
};

class ClickBarChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
        this.chartClick = new EventEmitter();
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.map((value, i) => (Object.assign(Object.assign({}, datasetDefaults$6(value, i, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults$4), { data: newData, options: Object.assign(Object.assign(Object.assign({}, chartDefaults$4.options), this.options), { plugins: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults$4.options.plugins), (data.datasets.length < 2
                    ? {
                        legend: {
                            display: false,
                        },
                    }
                    : {})), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }), onClick: (_event, elements, _chart) => {
                    elements[0] && this.chartClick.emit({ index: elements[0].index });
                } }) });
    }
}
ClickBarChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-click-bar-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
ClickBarChartComponent.ctorParameters = () => [];
ClickBarChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }],
    chartClick: [{ type: Output }]
};

const datasetDefaults$2 = (index, colorType) => {
    const color = index === 0
        ? ['#CCCCCC']
        : index === 1
            ? ['#BD0024'] : [];
    const hoverBackgroundColor = '#DF002A';
    const newIndex = index % color.length;
    switch (colorType) {
        case ColorType.POINT:
            return {
                label: '',
                data: [],
                backgroundColor: color,
                borderColor: color,
                hoverBackgroundColor: hoverBackgroundColor,
                fill: true,
            };
        case ColorType.SERIES:
        default:
            return {
                label: '',
                data: [],
                backgroundColor: color[newIndex],
                borderColor: color[newIndex],
                hoverBackgroundColor: hoverBackgroundColor,
                fill: true,
            };
    }
};
const ɵ0$3 = {
    datasets: [],
};
const chartDefaults$3 = {
    type: 'bar',
    data: ɵ0$3,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { grid: { display: false }, stacked: true },
            y: {
                stacked: false,
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
        },
        plugins: {
            legend: {
                reverse: false,
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};

class StackedProgressBarChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.reverse().map((value, i, array) => (Object.assign(Object.assign({}, datasetDefaults$2(array.length - 1 - i, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults$3), { data: newData, options: Object.assign(Object.assign(Object.assign({}, chartDefaults$3.options), this.options), { plugins: Object.assign(Object.assign(Object.assign({}, chartDefaults$3.options.plugins), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
StackedProgressBarChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-stacked-progress-bar-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
StackedProgressBarChartComponent.ctorParameters = () => [];
StackedProgressBarChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

const datasetDefaults$1 = (index, arrayLength, colorType) => {
    const color = (arrayLength === 5) ?
        (index === 4 ? ['#BD0024'] :
            (index === 3 ? ['#6C0510'] :
                (index === 2 ? ['#CCCCCC'] :
                    (index === 1 ? ['#878787'] : ['#525252'])))) :
        (arrayLength === 4) ?
            (index === 3 ? ['#BD0024'] :
                (index === 2 ? ['#6C0510'] :
                    (index === 1 ? ['#CCCCCC'] : ['#878787']))) :
            (arrayLength === 3) ?
                (index === 2 ? ['#BD0024'] :
                    (index === 1 ? ['#6C0510'] : ['#CCCCCC'])) :
                (arrayLength === 2) ?
                    (index === 1 ? ['#BD0024'] : ['#6C0510']) :
                    ['#BD0024', '#6C0510', '#CCCCCC', '#878787', '#525252'];
    const hoverBackgroundColor = '#DF002A';
    const newIndex = index % color.length;
    switch (colorType) {
        case ColorType.POINT:
            return {
                label: '',
                data: [],
                backgroundColor: color,
                borderColor: color,
                hoverBackgroundColor: hoverBackgroundColor,
                fill: true,
            };
        case ColorType.SERIES:
        default:
            return {
                label: '',
                data: [],
                backgroundColor: color[newIndex],
                borderColor: color[newIndex],
                hoverBackgroundColor: hoverBackgroundColor,
                fill: true,
            };
    }
};
const ɵ0$2 = {
    datasets: [],
};
const chartDefaults$2 = {
    type: 'bar',
    data: ɵ0$2,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        scales: {
            x: {
                stacked: false,
                beginAtZero: true,
                ticks: {
                    callback(tickValue, index, ticks) {
                        return abbreviateNumber(tickValue);
                    },
                },
            },
            y: {
                grid: {
                    display: false,
                },
                stacked: true,
            },
        },
        plugins: {
            legend: {
                reverse: false,
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};

class StackedProgressHorizontalBarChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.reverse().map((value, i, array) => (Object.assign(Object.assign({}, datasetDefaults$1(array.length - 1 - i, array.length, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults$2), { data: newData, options: Object.assign(Object.assign(Object.assign({}, chartDefaults$2.options), this.options), { plugins: Object.assign(Object.assign(Object.assign({}, chartDefaults$2.options.plugins), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
StackedProgressHorizontalBarChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-stacked-progress-horizontal-bar-chart',
                template: "<div class=\"la-chart\" style=\"position: relative\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: [""]
            },] }
];
StackedProgressHorizontalBarChartComponent.ctorParameters = () => [];
StackedProgressHorizontalBarChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

class VarianceSubtitleKpiComponent {
    constructor(kpiService, router) {
        this.kpiService = kpiService;
        this.router = router;
        this.title = '';
        this.subTitle = '';
        this.redirect = null;
    }
    ngOnInit() {
        this.value = this.transformResponse(this.response);
    }
    ngOnDestroy() {
        this.kpiService.reset();
    }
    transformResponse(response) {
        (response === null || response === void 0 ? void 0 : response.isNegative) && this.kpiService.setNegative();
        this.variance = response.delta;
        return response.value.toString();
    }
    route() {
        var _a, _b, _c, _d;
        ((_a = this.redirect) === null || _a === void 0 ? void 0 : _a.url) &&
            this.router.navigate([(_b = this.redirect) === null || _b === void 0 ? void 0 : _b.url], {
                queryParams: { filters: JSON.stringify((_d = (_c = this.redirect) === null || _c === void 0 ? void 0 : _c.filters) !== null && _d !== void 0 ? _d : {}) },
            });
    }
}
VarianceSubtitleKpiComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-variance-subtitle-kpi',
                template: "<div\r\n  class=\"center\"\r\n  [ngClass]=\"{ redirect: !!this.redirect?.url }\"\r\n  (click)=\"route()\"\r\n>\r\n  <div class=\"done\">\r\n    <h3 class=\"no-margin-top no-margin-bottom la-widget-header\">\r\n      {{ title }}\r\n    </h3>\r\n    <h1 class=\"margin-2 la-widget-value\">\r\n      {{ value }}\r\n    </h1>\r\n    <div class=\"la-widget-sub\">\r\n      <p class=\"no-margin-top no-margin-bottom la-widget-variance\">\r\n        {{ variance }}\r\n      </p>\r\n      <i\r\n          [ngClass]=\"\r\n            kpiService.isNegative ? 'pi pi-arrow-down' : 'pi pi-arrow-up'\r\n          \"\r\n        ></i>      \r\n    </div> \r\n    <p class=\"fontSize14px la-widget-subtitle\">\r\n      {{ subTitle }}\r\n    </p>   \r\n  </div>\r\n</div>",
                styles: [""]
            },] }
];
VarianceSubtitleKpiComponent.ctorParameters = () => [
    { type: KpiService },
    { type: Router }
];
VarianceSubtitleKpiComponent.propDecorators = {
    title: [{ type: Input }],
    subTitle: [{ type: Input }],
    response: [{ type: Input }],
    redirect: [{ type: Input }]
};

class DynamicStatusKpiComponent {
    constructor(kpiService, router) {
        this.kpiService = kpiService;
        this.router = router;
        this.title = '';
        this.redirect = null;
    }
    ngOnInit() {
        var _a;
        this.value = this.transformResponse(this.response);
        this.label = this.response.label;
        this.isNegativeFlag = this.response.isNegative;
        this.displayDate = (_a = this.response.maxDate) !== null && _a !== void 0 ? _a : null;
    }
    ngOnDestroy() {
        this.kpiService.reset();
    }
    transformResponse(response) {
        if (response === null || response === void 0 ? void 0 : response.isNegative) {
            this.kpiService.setNegative();
        }
        this.variance = response.delta;
        return response.value.toString();
    }
    route() {
        var _a, _b, _c, _d;
        ((_a = this.redirect) === null || _a === void 0 ? void 0 : _a.url) &&
            this.router.navigate([(_b = this.redirect) === null || _b === void 0 ? void 0 : _b.url], {
                queryParams: { filters: JSON.stringify((_d = (_c = this.redirect) === null || _c === void 0 ? void 0 : _c.filters) !== null && _d !== void 0 ? _d : {}) },
            });
    }
}
DynamicStatusKpiComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-dynamic-status-kpi',
                template: "<div\r\n  class=\"center\"\r\n  [ngClass]=\"{ redirect: !!this.redirect?.url }\"\r\n  (click)=\"route()\"\r\n>\r\n  <div class=\"done\">\r\n    <h3 class=\"no-margin-top no-margin-bottom la-widget-header\">\r\n      {{ label }}\r\n    </h3>\r\n    <!-- <h1 class=\"margin-2 la-widget-value\">\r\n      {{ value }}\r\n    </h1> -->\r\n    <div class=\"la-widget-sub\">\r\n      <!-- <p class=\"fontSize14px margin-top-5 la-widget-variance\">\r\n        {{ variance }}\r\n      </p> -->\r\n      <!-- <div class=\"margin-top-5\">\r\n        <div class=\"kpi-date\" *ngIf=\"displayDate\">Date: {{ displayDate }}</div>\r\n      </div> -->\r\n    </div>\r\n  </div>\r\n</div>\r\n",
                styles: [""]
            },] }
];
DynamicStatusKpiComponent.ctorParameters = () => [
    { type: KpiService },
    { type: Router }
];
DynamicStatusKpiComponent.propDecorators = {
    title: [{ type: Input }],
    response: [{ type: Input }],
    redirect: [{ type: Input }]
};

class TieredKpiComponent {
    constructor(router) {
        this.router = router;
        this.title = '';
        this.subTitle = '';
        this.redirect = null;
    }
    ngOnInit() {
        this.value = this.transformResponse(this.response);
    }
    transformResponse(response) {
        //transform
        return response.value;
    }
    route() {
        var _a, _b, _c, _d;
        ((_a = this.redirect) === null || _a === void 0 ? void 0 : _a.url) &&
            this.router.navigate([(_b = this.redirect) === null || _b === void 0 ? void 0 : _b.url], {
                queryParams: { filters: JSON.stringify((_d = (_c = this.redirect) === null || _c === void 0 ? void 0 : _c.filters) !== null && _d !== void 0 ? _d : {}) },
            });
    }
}
TieredKpiComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-tiered-kpi',
                template: "<div\r\n  class=\"center\"\r\n  [ngClass]=\"{ redirect: !!this.redirect?.url }\"\r\n  (click)=\"route()\"\r\n>\r\n  <div class=\"done\">\r\n    <h3 class=\"no-margin-top no-margin-bottom\">\r\n      {{ title }}\r\n    </h3>\r\n    <p class=\"fontSize14px margin-top-5\">\r\n      {{ subTitle }}\r\n    </p>\r\n    <h1 class=\"margin-2\">\r\n      {{ value }}\r\n    </h1>\r\n  </div>\r\n</div>\r\n",
                styles: [""]
            },] }
];
TieredKpiComponent.ctorParameters = () => [
    { type: Router }
];
TieredKpiComponent.propDecorators = {
    title: [{ type: Input }],
    subTitle: [{ type: Input }],
    response: [{ type: Input }],
    redirect: [{ type: Input }]
};

const datasetDefaults = (index, colorType) => {
    const color = ['#BD0024', '#6C0510', '#CCCCCC', '#878787', '#525252'];
    const hoverBackgroundColor = '#DF002A';
    const newIndex = index % color.length;
    switch (colorType) {
        case ColorType.POINT:
            return {
                label: '',
                data: [],
                backgroundColor: color,
                borderColor: color,
                hoverBackgroundColor: hoverBackgroundColor,
            };
        case ColorType.SERIES:
        default:
            return {
                label: '',
                data: [],
                backgroundColor: color[newIndex],
                borderColor: color[newIndex],
                hoverBackgroundColor: hoverBackgroundColor,
            };
    }
};
const ɵ0$1 = {
    datasets: [],
};
const chartDefaults$1 = {
    type: 'doughnut',
    data: ɵ0$1,
    options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
        layout: {
            padding: 0,
        },
    },
};

class DoughnutChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
        this.data2 = [];
    }
    ngOnInit() {
        //console.log('ngOnInit')
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        //console.log('ngAfterViewInit')
        //console.log(this.calculateTotalCount(), 'calculations')
        //console.log('AfterDraw')
        if (this.chart && this.chart != null) {
            this.chart.destroy();
        }
        //this.chartConfig.plugins = [doughnut_waffles];
        this.chart = new Chart(this.chartRef.canvas, this.chartConfig);
    }
    calculateTotalCount() {
        let totalCount = 0;
        //console.log(this.data.datasets, 'dataset used for calculation');
        // Loop through each dataset
        for (let dataset of this.data.datasets) {
            if (Array.isArray(dataset.data)) {
                // Loop through the data of the current dataset
                for (let value of dataset.data) {
                    // Sum up valid (non-undefined) values
                    totalCount += value || 0;
                }
            }
        }
        return totalCount;
    }
    buildChartConfig(data) {
        const color = [
            '#BD0024',
            '#6C0510',
            '#CCCCCC',
            '#878787',
            '#525252',
            '#000000',
            '#FBBBC2',
            '#FEEDEF',
        ];
        const hoverBackgroundColor = '#DF002A';
        const combinedData = data.datasets.reduce((acc, dataset) => {
            if (Array.isArray(dataset.data)) {
                // Filter out undefined values from the dataset
                const filteredData = dataset.data.filter((value) => value !== undefined);
                return acc.concat(filteredData); // Only add valid values
            }
            return acc; // If dataset.data is not an array, do not include it
        }, []);
        // Ensure combinedData is not empty
        if (combinedData.length === 0) {
            console.error('No valid data found in datasets:', data.datasets);
        }
        // console.log(this.data, 'data coming in');
        // console.log(combinedData, 'combined data');
        const combinedBackgroundColor = combinedData.map((_, index) => color[index % color.length]);
        const newData = {
            labels: data.labels,
            datasets: [
                {
                    data: combinedData,
                    backgroundColor: combinedBackgroundColor,
                    borderColor: combinedBackgroundColor,
                    hoverBackgroundColor: hoverBackgroundColor,
                },
            ],
        };
        this.data2 = Object.assign({}, data);
        //console.log('deep copy object')
        //console.log(newData, 'newData');
        return Object.assign(Object.assign({}, chartDefaults$1), { data: newData, options: Object.assign(Object.assign({}, chartDefaults$1.options), { plugins: Object.assign(Object.assign({}, chartDefaults$1.options.plugins), { tooltip: {
                        callbacks: {
                            label: (tooltipItem) => {
                                const dataIndex = tooltipItem.dataIndex;
                                let label = data.labels[dataIndex];
                                // If the label is missing or undefined, use a default dynamic label
                                if (!label) {
                                    label = `Item ${dataIndex + 1}`; // Fallback to "Item X"
                                }
                                // Get the corresponding value for the current data point
                                const value = combinedData[dataIndex];
                                // Return the formatted tooltip string with label and value
                                return `${label}: ${value}`;
                            },
                        },
                    } }) }), plugins: [doughnut_waffles] });
    }
}
DoughnutChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-doughnut-chart',
                template: "<div class=\"la-chart-container\">\r\n  <canvas laChart></canvas>\r\n</div>\r\n",
                styles: ["canvas{width:100%!important;height:432px!important}\n"]
            },] }
];
DoughnutChartComponent.ctorParameters = () => [];
DoughnutChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }],
    countText: [{ type: Input }]
};
function calculateTotalCount2(data) {
    let totalCount = 0;
    //console.log(data.datasets, 'dataset used for calculation');
    // Loop through each dataset
    for (let dataset of data.datasets) {
        if (Array.isArray(dataset.data)) {
            // Loop through the data of the current dataset
            for (let value of dataset.data) {
                // Sum up valid (non-undefined) values
                totalCount += value || 0;
            }
        }
    }
    return totalCount;
}
const doughnut_waffles = {
    id: 'doughnut_waffles',
    beforeDraw(chart, args, options) {
        var chartType = chart.config.type;
        if (chartType === 'doughnut') {
            var { width, height, ctx } = chart;
            ctx.restore();
            ctx.font = '50px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#333';
            //console.log(calculateTotalCount2(chart.data), 'total');
            // console.log(this.data.datasets, 'dataset');
            var text = calculateTotalCount2(chart.data).toString() || '0';
            var textWidth = ctx.measureText(text).width;
            var x = (width - textWidth) / 2.2;
            var y = height / 2;
            ctx.fillText(text, x, y);
            ctx.save();
        }
    },
};

const ɵ0 = {
    datasets: [],
};
const chartDefaults = {
    type: 'bar',
    data: ɵ0,
    options: {
        responsive: false,
        maintainAspectRatio: false,
        scales: {
            y: {
                grid: { display: false },
                ticks: {
                    autoSkip: false, // Ensure all ticks are shown
                },
            },
            x: {
                beginAtZero: true,
                // grid: { drawTicks: false },
                // ticks: {
                //   callback(tickValue, index, ticks) {
                //     return abbreviateNumber(tickValue as number);
                //   },
                // },
            },
        },
        indexAxis: 'y',
        plugins: {
            legend: {
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                },
            },
        },
    },
};

class HorizontalBarScrollChartComponent {
    constructor() {
        this.colorType = ColorType.SERIES;
    }
    ngOnInit() {
        this.chartConfig = this.buildChartConfig(this.data);
    }
    ngAfterViewInit() {
        // Build chart configurations using the existing buildChartConfig logic
        const chart1Config = this.buildChartConfig(this.data);
        const chart2Config = this.buildChartConfig(this.data);
        setTimeout(() => {
            if (this.chart1 && this.chart1.data) {
                this.adjustScrollBoxHeight();
            }
        }, 100);
        // Create the first chart
        this.chart1 = new Chart(this.chartCanvas1.nativeElement, Object.assign(Object.assign({}, chart1Config), { options: Object.assign(Object.assign({}, chart1Config.options), { responsive: true, maintainAspectRatio: false, 
                // layout: {
                //   padding: {
                //     right: 10,
                //   },
                // },
                scales: {
                    x: Object.assign(Object.assign({}, chart1Config.options.scales.x), { display: true, grid: {
                            display: true,
                            drawBorder: false,
                            drawTicks: false,
                        }, ticks: {
                            display: false, // Ensure all ticks are shown
                        } }),
                    y: Object.assign(Object.assign({}, chart1Config.options.scales.y), { ticks: {
                            display: true, // Ensure all ticks are shown
                        } }),
                } }) }));
        this.chart2 = new Chart(this.chartCanvas2.nativeElement, {
            type: 'bar',
            data: {
                datasets: [
                    {
                        data: chart1Config.data.datasets[0].data,
                    },
                ],
            },
            options: Object.assign(Object.assign({}, chart2Config.options), { responsive: true, maintainAspectRatio: false, indexAxis: 'y', scales: {
                    x: {
                        afterFit: (context) => {
                            context.height += 30;
                        },
                        ticks: {
                            display: true,
                        },
                    },
                    y: {
                        beginAtZero: true,
                        afterFit: (context) => {
                            context.width += this.chart1.chartArea.left; //this.chartCanvas1.nativeElement.chartArea.left;
                        },
                        grid: {
                            drawTicks: false,
                        },
                    },
                } }),
        });
        setTimeout(() => {
            // this.alignChart2XAxis();
            this.adjustScrollBoxHeight();
        }, 0);
    }
    adjustScrollBoxHeight() {
        var _a, _b;
        // Ensure the chartCanvas1 data is available
        if ((_b = (_a = this.chart1) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.labels) {
            const scrollBoxBody = document.querySelector('.scrollBoxBody');
            if (scrollBoxBody) {
                // Calculate height dynamically based on the number of labels
                const baseHeight = 434; // Base height for 4 labels
                const extraHeight = (this.chart1.data.labels.length - 4) * 20; // Height per additional label
                const newHeight = baseHeight + Math.max(0, extraHeight); // Ensure height doesn't decrease
                scrollBoxBody.style.height = `${newHeight}px`;
            }
        }
    }
    buildChartConfig(data) {
        var _a;
        const newData = Object.assign(Object.assign({}, data), { datasets: [
                ...data.datasets.map((value, i) => (Object.assign(Object.assign({}, datasetDefaults$6(value, i, this.colorType)), value))),
            ] });
        return Object.assign(Object.assign({}, chartDefaults), { data: newData, options: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults.options), { grouped: false }), this.options), { plugins: Object.assign(Object.assign(Object.assign(Object.assign({}, chartDefaults.options.plugins), (data.datasets.length < 2
                    ? {
                        legend: {
                            display: false,
                        },
                    }
                    : {})), (((_a = this.options) === null || _a === void 0 ? void 0 : _a.plugins) || {})), { tooltip: {
                        callbacks: {
                            label(tooltipItem) {
                                return [
                                    ...data.datasets[tooltipItem.datasetIndex].tooltips[tooltipItem.dataIndex].map(({ label, value }) => `${label}: ${value}`),
                                ];
                            },
                        },
                    } }) }) });
    }
}
HorizontalBarScrollChartComponent.decorators = [
    { type: Component, args: [{
                selector: 'lbmx-horizontal-bar-scroll-chart',
                template: "<div class=\"chart-scroll-container\">\r\n  <div class=\"chartBox\">\r\n    <div class=\"scrollBox\">\r\n      <div class=\"scrollBoxBody\">\r\n        <canvas #chartCanvas1 laChart></canvas>\r\n      </div>\r\n    </div>\r\n    <div class=\"box\">\r\n      <canvas #chartCanvas2 laChart></canvas>\r\n    </div>\r\n  </div>\r\n</div>\r\n",
                styles: [".chart-scroll-container{height:100%;width:100%}.scrollBox{max-height:434px;overflow-y:auto;padding-right:10px}canvas{width:100%!important;height:auto!important;display:block}.box{height:50px}.scrollBoxBody{height:auto}\n"]
            },] }
];
HorizontalBarScrollChartComponent.ctorParameters = () => [];
HorizontalBarScrollChartComponent.propDecorators = {
    chartRef: [{ type: ViewChild, args: [ChartDirective,] }],
    chartCanvas1: [{ type: ViewChild, args: ['chartCanvas1',] }],
    chartCanvas2: [{ type: ViewChild, args: ['chartCanvas2',] }],
    data: [{ type: Input }],
    options: [{ type: Input }],
    colorType: [{ type: Input }]
};

class AnalyticsModule {
}
AnalyticsModule.decorators = [
    { type: NgModule, args: [{
                declarations: [
                    AnalyticsDirective,
                    BaseKpiComponent,
                    ChartDirective,
                    DashboardWrapperComponent,
                    GaugeComponent,
                    KpiComponent,
                    WidgetDirective,
                    WidgetWrapperComponent,
                    VarianceGaugeKpiComponent,
                    VarianceKpiComponent,
                    TabWidgetWrapperComponent,
                    WidgetComponent,
                    TabChartComponent,
                    TabChartBodyComponent,
                    TabBaseChartComponent,
                    TabHorizontalBarChartComponent,
                    KpiBodyComponent,
                    StackedBarChartComponent,
                    StackedHorizontalBarChartComponent,
                    StackedAreaChartComponent,
                    TableGridComponent,
                    ProjectedChartComponent,
                    GaugeVarianceComponent,
                    ClickBarChartComponent,
                    StackedProgressBarChartComponent,
                    StackedProgressHorizontalBarChartComponent,
                    VarianceSubtitleKpiComponent,
                    DynamicStatusKpiComponent,
                    TieredKpiComponent,
                    DoughnutChartComponent,
                    HorizontalBarScrollChartComponent,
                    ColoredBaseKpiComponent,
                ],
                imports: [CdTableModule, CommonModule],
                exports: [
                    AnalyticsDirective,
                    DashboardWrapperComponent,
                    KpiComponent,
                    GaugeComponent,
                    WidgetDirective,
                    WidgetWrapperComponent,
                    TabWidgetWrapperComponent,
                    TabChartComponent,
                    CdTableModule,
                ],
                providers: [HttpClient],
            },] }
];

// export interface CdColumn {

/*
 * Public API Surface of analytics
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AnalyticsDirective, AnalyticsModule, AnalyticsStatus, CdTableModule, ColorType, DashboardWrapperComponent, ExampleDataSource, GaugeComponent, KpiComponent, TabChartComponent, TabWidgetWrapperComponent, TableComponent, TemplateTypes, WidgetDirective, WidgetWrapperComponent, abbreviateNumber, BaseKpiComponent as ɵa, ChartDirective as ɵb, DoughnutChartComponent as ɵba, HorizontalBarScrollChartComponent as ɵbb, ColoredBaseKpiComponent as ɵbc, KpiService as ɵc, RequestService as ɵd, VarianceGaugeKpiComponent as ɵf, VarianceKpiComponent as ɵg, LoadOnceService as ɵh, WidgetComponent as ɵi, TabChartBodyComponent as ɵk, TabBaseChartComponent as ɵl, TabHorizontalBarChartComponent as ɵm, KpiBodyComponent as ɵn, StackedBarChartComponent as ɵo, StackedHorizontalBarChartComponent as ɵp, StackedAreaChartComponent as ɵq, TableGridComponent as ɵr, ProjectedChartComponent as ɵs, GaugeVarianceComponent as ɵt, ClickBarChartComponent as ɵu, StackedProgressBarChartComponent as ɵv, StackedProgressHorizontalBarChartComponent as ɵw, VarianceSubtitleKpiComponent as ɵx, DynamicStatusKpiComponent as ɵy, TieredKpiComponent as ɵz };
//# sourceMappingURL=lbmx-analytics.js.map
